import React, { useState, useEffect, useCallback, useRef } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import axios from 'axios';
import { useSnackbar } from 'notistack';
import styled from "@emotion/styled";
import CloseIcon from '@mui/icons-material/Close';
import StraightenIcon from '@mui/icons-material/Straighten';
import ClearIcon from '@mui/icons-material/Clear';
// MUI components
import {
  Box,
  Typography,
  CircularProgress,
  Card,
  CardContent,
  CardMedia,
  Grid,
  Container,
  Paper,
  Button,
  IconButton,
  Chip,
  TableContainer,
  Table,
  TableBody,
  TableCell,
  TableRow,
  Rating,
  useTheme,
  useMediaQuery,
  ToggleButtonGroup,
  ToggleButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Pagination,
  Select,
  MenuItem,
  Tooltip,
  Breadcrumbs,
  Link,
  TextField,
  Snackbar,
  Alert,
  Tabs,
  Tab,
  Modal,
  Autocomplete,
  RadioGroup,
  FormControlLabel,
  FormLabel,
  Radio,
  FormControl,
  InputLabel,
  Fade,
  TableHead,
  Button as MuiButton,
} from "@mui/material";

// MUI icons
import {
  Favorite,
  FavoriteBorder,
  LocalGasStation,
  DirectionsCar,
  GridView,
  List as ListIcon,
  AddShoppingCart,
  ChatBubbleOutline,
  Add,
  Remove,
  Share,
  ArrowBackIos,
  ArrowForwardIos,
  Close,

} from "@mui/icons-material";
import ReceiptIcon from "@mui/icons-material/Receipt";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import CalculateIcon from "@mui/icons-material/Calculate";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import DirectionsCarIcon from "@mui/icons-material/DirectionsCar";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import FilterListIcon from "@mui/icons-material/FilterList";
import PrecisionManufacturingIcon from "@mui/icons-material/PrecisionManufacturing";
// Local components
import Notes from "./header";
import BasicSelectFilter from "./filter";
import FooterManual from "./footerManual";
import NewNotes from "./newHeader";
import { InputAdornment } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';

// Redux actions
import {
  addToWishlist,
  fetchWishlist,
  fetchCart,
  // FIX: Renamed/Consolidated performMoreFilter to performAdvanceFilter
  performAdvanceFilter,
  fetchCarsByModelName
} from "./store";

// Styled Components from ExploreByGlobalSearch
const StyledTableContainer = styled(TableContainer)`
  box-shadow: 0px 4px 8px rgba(0, 0, 0, 0.1);
  border-radius: 12px;
  margin-bottom: 16px;
  overflow-x: auto;
`;

const StyledTableRow = styled(TableRow)`
  cursor: pointer;
  transition: background-color 0.2s ease-in-out;
  &:hover {
    background-color: rgba(0, 0, 0, 0.04);
  }
`;

const StyledTableHeaderCell = styled(TableCell)`
  background-color: #f5f5f5;
  font-weight: bold;
  position: sticky;
  top: 0;
  z-index: 10;
`;

const StyledCard = styled(Card)`
  height: 100%;
  display: flex;
  flex-direction: column;
  cursor: pointer;
  border-radius: 10px;
  overflow: hidden;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
  transition: all 0.3s ease;
  &:hover {
    box-shadow: 0 8px 16px rgba(0, 0, 0, 0.2);
    transform: translateY(-2px);
  }
`;

const StickyFilterContainer = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 1000;
  background-color: white;
  box-shadow: 0px 2px 10px rgba(0, 0, 0, 0.1);
  transition: transform 0.3s ease-in-out;
  transform: ${(props) =>
    props.visible ? "translateY(0)" : "translateY(-100%)"};
`;

const SpinningLogo = styled.img`
  width: 60px;
  height: 60px;
  animation: spin 1.5s linear infinite;
  @keyframes spin {
    0% {
      transform: rotate(0deg);
    }
    100% {
      transform: rotate(360deg);
    }
  }
`;

// Car Card for List View
const CarListingCardGrid = ({
  car,
  onCarClick,
  onAddToWishlist,
  onAddToCart,
  imageUrl,
}) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();
  const { modelId } = useParams();
  const [carImages, setCarImages] = useState({});
  const cartItems = useSelector((state) => state.cart.items);
  const wishlistItems = useSelector((state) => state.wishlist.items);
  const isFavorite = wishlistItems.some((item) => item.modelId === car.carModelId);
  const [selectedCarModelId, setSelectedCarModelId] = useState(null);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  // Dialog and Modal Handling
  const [openDialog, setOpenDialog] = useState(false);
  const [dialogType, setDialogType] = useState("");
  const [isHovered, setIsHovered] = useState(false);
  const [selectedModelId, setSelectedModelId] = useState(null);
  const [open, setOpen] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalImageIndex, setModalImageIndex] = useState(0);

  // Product and Loading
  const [product, setProduct] = useState(null);
  const [productLoading, setProductLoading] = useState(true);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Image & View
  const [currentImageType, setCurrentImageType] = useState('exterior');
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [imageError, setImageError] = useState({});
  const [showDetails, setShowDetails] = useState(false);

  // Inquiry and Form
  const [inquiryType, setInquiryType] = useState('general');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showErrorPopup, setShowErrorPopup] = useState(false);
  const [showSuccessPopup, setShowSuccessPopup] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isPortSelected, setIsPortSelected] = useState(false);
  const [shippingCostData, setShippingCostData] = useState(null);

  const [userDetails, setUserDetails] = useState(null);
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    emailId: "",
    phoneNumber: "",
    description: "",
    country: null,
    port: null,
    userMessage: '',
    shippingOption: ''
  });

  const [countriesList, setCountriesList] = useState([]);
  const [portsList, setPortsList] = useState([]);

  // Handle port selection
  const handlePortChange = (event, newValue) => {
    handleChange({ target: { name: 'port', value: newValue } });
    setIsPortSelected(!!newValue);
  };




  // <------------------ user details api integration code start ---------------->

  const [isLoadingUserDetails, setIsLoadingUserDetails] = useState(false);

  // Add this function to fetch user details
  const fetchUserDetailsGlobal = async () => {
    try {
      // Get the token from localStorage or wherever you store it
      const token = localStorage.getItem('authToken') || 'eyJhbGciOiJIUzUxMiJ9.eyJzdWIiOiJoYXJkaWtAZ3JldHRvbS5jb20iLCJleHAiOjE3NTQ4NzY2NjEsImlhdCI6MTc1MTI3NjY2MSwiYXV0aG9yaXRpZXMiOiIifQ.caR99Ex3qtHfp9tLCMbsDxf9uxt_KhK9IlDbrB9-rek498PHySU1-FQhmBmTtK947DnUBXKS6CwNy6PccCH-4A';

      const response = await fetch('https://car.amar.co.jp/api/authorization/user/get/user/details', {
        method: 'GET',
        headers: {
          'accept': 'application/json',
          'Accept-Language': 'en',
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const userData = await response.json();
      return userData;

    } catch (error) {
      console.error('Error fetching user details:', error);
      throw error;
    }
  };

  // Add this useEffect to populate form data when component mounts
  useEffect(() => {
    const loadUserDetails = async () => {
      try {
        setIsLoadingUserDetails(true);
        const response = await fetchUserDetailsGlobal();
        const userDetails = response.userDetails;

        // Map API response to your form fields
        // Adjust the field names based on your API response structure
        setFormData(prevData => ({
          ...prevData,
          firstName: userDetails.firstName || '',
          lastName: userDetails.lastName || '',
          phoneNumber: userDetails.userMobileNumber || '',
          emailId: userDetails.email || ''
        }));

      } catch (error) {
        console.error('Failed to load user details:', error);
        // Optionally show error message to user
      } finally {
        setIsLoadingUserDetails(false);
      }
    };

    // Only fetch if inquiryType is 'general' and form is empty
    if (inquiryType === 'general' && !formData.firstName) {
      loadUserDetails();
    }
  }, [inquiryType]); // Run when inquiryType changes

  // Your existing handleInputChange function
  const handleInputChangeGlobal = (field) => (event) => {
    setFormData(prev => ({
      ...prev,
      [field]: event.target.value
    }));
  };

  // Add this function to manually refresh user details
  const handleRefreshUserDetails = async () => {
    try {
      setIsSubmitting(true);
      const userDetails = await fetchUserDetailsGlobal();

      setFormData(prevData => ({
        ...prevData,
        firstName: userDetails.firstName || userDetails.first_name || '',
        lastName: userDetails.lastName || userDetails.last_name || '',
        phoneNumber: userDetails.phoneNumber || userDetails.phone || userDetails.mobile || '',
        emailId: userDetails.emailId || userDetails.email || userDetails.emailAddress || ''
      }));

      console.log('User details loaded successfully');

    } catch (error) {
      console.error('Failed to refresh user details:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  // <------------------ user details api integration code end ---------------->

  const styles = {
    globalWrapper: {
      width: '100%',
      maxWidth: '100%',
      padding: 0,
    },

  };


  const currentModelId = modelId || car?.carModelId;

  const countries = ["AUSTRALIA", "ARUBA", "BAHAMAS", "BERMUDA", "CANADA", "CHILE", "DENMARK", "DOMINICA", "ETHIOPIA", "FIJI"];

  const shippingOptions = [
    { value: "CIF", label: "CIF (Cost, Insurance and Freight)" },
    { value: "CNF", label: "C&F (Cost and Freight)" },
    { value: "FOB", label: "FOB (Free On Board)" }
  ];
  const feeConfigurations = [
    { key: 'INSURANCE', label: 'Insurance', apiKey: 'insuranceCost' },
    { key: 'DOCUMENTATION', label: 'Documentation', apiKey: 'documentationFee' },
    { key: 'CURRENCY_ADJUSTMENT_FACTOR', label: 'Currency Adjustment', apiKey: 'currencyAdjustmentFactor' },
    { key: 'BUNKER_ADJUSTMENT_FACTOR', label: 'Bunker Adjustment', apiKey: 'bunkerAdjustmentFactor' },
    { key: 'TERMINAL_HANDLING', label: 'Terminal Handling', apiKey: 'terminalHandlingCharge' },
    { key: 'CUSTOMS_CLEARANCE', label: 'Customs Clearance', apiKey: 'customsClearanceFee' },
    { key: 'baseCostPerM3', label: 'Base Cost per m³', apiKey: 'baseShippingCost', isBaseCost: true }
  ];

  const labelMap = {
    firstName: "First Name *",
    lastName: "Last Name *",
    phoneNumber: "Phone Number *",
    emailId: "Email Address *"
  };

  // Fetch countries when Country Dropdown is opened
  const fetchCountries = async () => {
    try {
      const response = await fetch('https://car.amar.co.jp/api/authorization/shipping/countries');
      const data = await response.json();
      setCountriesList(data);
    } catch (error) {
      console.error('Error fetching countries:', error);
    }
  };

  // Fetch ports when Port Dropdown is opened
  const fetchPorts = async (countryId) => {
    try {
      const response = await fetch(`https://car.amar.co.jp/api/authorization/shipping/ports/country/${countryId}`);
      const data = await response.json();
      setPortsList(data);
    } catch (error) {
      console.error('Error fetching ports:', error);
    }
  };

  const fetchUserDetails = async (token) => {
    try {
      const response = await axios.get('https://car.amar.co.jp/api/authorization/user/details', {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      setUserDetails(response.data.data);
    } catch (error) {
      console.error('Failed to fetch user details:', error);
      // If token is invalid, remove it
      localStorage.removeItem('token');
      setUserDetails(null);
    }
  };

  // First, check token when component mounts
  useEffect(() => {
    const token = localStorage.getItem("authToken");
    if (token) {
      fetchUserDetails(token).catch(error => {
        console.error('Failed to fetch user details dddd:', error);
        localStorage.removeItem('token'); // Remove invalid token
      });
    }
  }, []);

  // Then fetch product data
  useEffect(() => {
    const fetchProductData = async () => {
      const currentModelId = selectedCarModelId || modelId;
      if (!currentModelId) return;

      try {
        setProductLoading(true);
        console.log('Fetching product data for modelId:', currentModelId);

        const response = await axios.get(
          `https://car.amar.co.jp/api/dashboard/product/get/Model/details/${currentModelId}`
        );

        console.log('Product API Response:', response.data); // Debug log

        if (response.data && response.data.data) {
          setProduct(response.data.data);
        } else {
          console.error('No product data received');
          setProduct(null);
        }

        setProductLoading(false);
      } catch (err) {
        setError('Failed to fetch product data. Please try again later.');
        setProductLoading(false);
        console.error('Error fetching product data:', err);
        setProduct(null);
      }
    };

    // Only fetch when modal is open and we have a selected model ID
    if (open && selectedCarModelId) {
      fetchProductData();
    }
  }, [open, selectedCarModelId, modelId]);


  const handleChange = (event) => {
    const { name, value } = event.target;
    setFormData(prev => ({
      ...prev,
      [name]: value,
      ...(name === 'country' && { port: null }) // Reset port when country changes
    }));
  };

  // Send shipping inquiry with token
  const sendShippingInquiry = async (token) => {
    const headers = {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    };

    const payload = {
      carModelId: parseInt(currentModelId, 10),
      portId: formData.port?.portId,
      shippingOption: formData.shippingOption,
      isSendInquiry: true,
      firstName: formData.firstName,
      lastName: formData.lastName,
      userMobileNumber: formData.phoneNumber,
      userMessage: formData.userMessage,
      userEmailId: formData.emailId
    };

    console.log('Sending shipping inquiry:', payload); // Add logging

    const response = await fetch('https://car.amar.co.jp/api/authorization/shipping/calculate', {
      method: 'POST',
      headers,
      body: JSON.stringify(payload)
    });

    if (!response.ok) {
      const errorText = await response.text();
      console.error('Shipping inquiry error:', errorText);
      throw new Error(`Shipping inquiry failed: ${response.status}`);
    }

    return response.json();
  };


  useEffect(() => {
    const calculateShippingCost = async () => {
      if (currentModelId && formData.port?.portId && formData.shippingOption) { // Use currentModelId instead of modelId
        try {
          const response = await fetch('https://car.amar.co.jp/api/authorization/shipping/calculate', {
            method: 'POST',
            headers: {
              'accept': 'application/json',
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              carModelId: parseInt(currentModelId, 10), // Use currentModelId here too
              portId: formData.port.portId,
              shippingOption: formData.shippingOption,
              isSendInquiry: false
            })
          });

          if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
          }

          const data = await response.json();
          console.log('Shipping cost data:', data); // Add logging
          setShippingCostData(data);
        } catch (error) {
          console.error('Error calculating shipping cost:', error);
          setShippingCostData(null); // Reset on error
        }
      } else {
        setShippingCostData(null); // Reset when conditions not met
      }
    };

    calculateShippingCost();
  }, [currentModelId, formData.port, formData.shippingOption]);



  const sendGeneralInquiry = async (token) => {
    const headers = {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    };

    // Fix: Use currentModelId instead of modelId
    const payload = {
      firstName: formData.firstName,
      lastName: formData.lastName,
      emailId: formData.emailId,
      phoneNumber: formData.phoneNumber,
      description: formData.userMessage,
      modelId: parseInt(currentModelId, 10) // Use currentModelId here
    };

    console.log('Debug - General Inquiry Payload:', payload); // Add debugging

    const response = await fetch('https://car.amar.co.jp/api/dashboard/inquiry/send-inquiry', {
      method: 'POST',
      headers,
      body: JSON.stringify(payload)
    });

    if (!response.ok) throw new Error('Inquiry failed');
  };

  const handleInputChange = (field) => (e) => {
    setFormData({
      ...formData,
      [field]: e.target.value
    });
  };

  // 3. Add validation in handleSubmitInquiry
  const handleSubmitInquiry = async () => {
    const token = localStorage.getItem("authToken");
    if (!token) {
      alert('Please login to submit inquiry');
      return;
    }

    // Add validation for modelId
    if (!currentModelId) {
      alert('Car model information is missing. Please try again.');
      return;
    }

    setIsSubmitting(true);

    try {
      if (inquiryType === 'general') {
        await sendGeneralInquiry(token);
      } else {
        await sendShippingInquiry(token);
      }
      setShowSuccessPopup(true);
    } catch (error) {
      console.error('Inquiry submission error:', error);
      setShowErrorPopup(true);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleModalOpen = () => setOpen(true);
  const handleModalClose = () => setOpen(false);



  const handleConfirmMove = async () => {
    try {
      const token = localStorage.getItem("authToken");
      if (!token) {
        navigate("/login");
        return;
      }

      const response = await fetch(
        `https://car.amar.co.jp/api/master/car-cart-and-whishlist/manage/item?modelId=${car.carModelId}&SelectType=MOVE_TO_CART`,
        {
          method: "POST",
          headers: {
            accept: "application/json",
            "Accept-Language": "en",
            Authorization: `Bearer ${token}`,
          },
          body: "",
        }
      );

      if (response.ok) {
        enqueueSnackbar("Item moved to cart successfully", {
          variant: "success",
        });
        dispatch(fetchWishlist());
        dispatch(fetchCart());
      } else {
        const data = await response.json();
        enqueueSnackbar(data.message || "Failed to move item to cart", {
          variant: "error",
        });
      }
    } catch (error) {
      enqueueSnackbar("Error moving item to cart", { variant: "error" });
      console.error("Error moving to cart:", error);
    } finally {
      setOpenDialog(false);
    }
  };

  const handleConfirmRemove = async () => {
    try {
      const token = localStorage.getItem("authToken");
      if (!token) {
        navigate("/login");
        return;
      }

      const response = await fetch(
        `https://car.amar.co.jp/api/master/car-cart-and-whishlist/delete/item?Enter%20ModelId=${car.carModelId}`,
        {
          method: "DELETE",
          headers: {
            accept: "application/json",
            "Accept-Language": "en",
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response.ok) {
        enqueueSnackbar("Item removed from wishlist successfully", {
          variant: "success",
        });
        dispatch(fetchWishlist());
      } else {
        const data = await response.json();
        enqueueSnackbar(data.message || "Failed to remove item from wishlist", {
          variant: "error",
        });
      }
    } catch (error) {
      enqueueSnackbar("Error removing item from wishlist", {
        variant: "error",
      });
      console.error("Error removing from wishlist:", error);
    } finally {
      setOpenDialog(false);
    }
  };

  const handleFavoriteClick = async (e) => {
    e.stopPropagation();
    try {
      const token = localStorage.getItem("authToken");
      if (!token) {
        navigate("/login");
        return;
      }
      if (isFavorite) {
        setDialogType("remove");
        setOpenDialog(true);
        return;
      }
      const result = await dispatch(addToWishlist(car.carModelId));
      if (result.success) {
        enqueueSnackbar("Added to wishlist successfully", {
          variant: "success",
        });
      } else {
        enqueueSnackbar(result.message || "Failed to add to wishlist", {
          variant: "error",
        });
      }
    } catch (error) {
      enqueueSnackbar("Error adding to wishlist", { variant: "error" });
      console.error("Error adding to wishlist:", error);
    }
  };

  return (
    <StyledCard
      // onClick={() => onCarClick(car.carModelId)}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      sx={{
        "&:hover": {
          boxShadow: "0 8px 24px rgba(0,0,0,0.15)",
          transform: "translateY(-4px)",
        },
        transition: "all 0.3s cubic-bezier(0.25, 0.8, 0.25, 1)",
      }}
    >
      {/* Image Section with Hover Effect */}
      <Box
        sx={{
          position: "relative",
          overflow: "hidden",
        }}
        onClick={() => onCarClick(car.carModelId)}
      >
        <CardMedia
          component="img"
          height="100%"
          image={imageUrl}
          alt={`${car.carBrandName} ${car.carModelName}`}
          sx={{
            transition: "transform 0.5s ease",
            transform: isHovered ? "scale(1.05)" : "scale(1)",
            objectFit: "cover",
          }}
        />

        {/* Favorite Button */}
        <IconButton
          sx={{
            position: "absolute",
            top: 8,
            right: 8,
            backgroundColor: "rgba(255,255,255,0.9)",
            padding: isMobile ? "4px" : "8px",
            "&:hover": {
              backgroundColor: "rgba(255,255,255,1)",
              transform: "scale(1.1)",
            },
            transition: "all 0.2s ease",
          }}
          onClick={handleFavoriteClick}
        >
          {isFavorite ? (
            <Favorite
              sx={{
                color: "red",
                fontSize: isMobile ? "1rem" : "1.25rem",
                animation: isFavorite ? "pulse 0.5s ease" : "none",
                "@keyframes pulse": {
                  "0%": { transform: "scale(1)" },
                  "50%": { transform: "scale(1.2)" },
                  "100%": { transform: "scale(1)" },
                },
              }}
            />
          ) : (
            <FavoriteBorder
              sx={{
                fontSize: isMobile ? "1rem" : "1.25rem",
                color: theme.palette.grey[600],
              }}
            />
          )}
        </IconButton>

        {/* Badges Overlay */}
        <Box
          sx={{
            position: "absolute",
            bottom: 8,
            left: 8,
            display: "flex",
            gap: 0.5,
            flexWrap: "wrap",
          }}
        >
          {car.ourPick && (
            <Chip
              label="Our Pick"
              size="small"
              sx={{
                fontSize: isMobile ? "0.55rem" : "0.65rem",
                height: isMobile ? "16px" : "18px",
                color: "#FF69B4", // Neon pink
                backgroundColor: "#4A0026",
                fontWeight: "bold",
                border: "1px solid #FF69B4",
                "&:hover": { backgroundColor: "white" },
              }}
            />
          )}
          {car.carGrade && (
            <Chip
              label={`Grade ${car.carGrade}`}
              size="small"
              sx={{
                fontSize: isMobile ? "0.55rem" : "0.65rem",
                height: isMobile ? "16px" : "18px",
                color: "#00FFAA", // Neon mint
                backgroundColor: "#00382A",
                fontWeight: "bold",
                border: "1px solid #00FFAA",
                "&:hover": { backgroundColor: "white" },
              }}
            />
          )}
          {car.specialDeal && (
            <Chip
              label="Special Offer"
              size="small"
              sx={{
                fontSize: isMobile ? "0.55rem" : "0.65rem",
                height: isMobile ? "16px" : "18px",
                backgroundColor: "#26004A",
                color: "#9D70FF", // Periwinkle
                fontWeight: "bold",
                border: "1px solid #9D70FF",
                "&:hover": { backgroundColor: "white" },
              }}
            />
          )}

        </Box>
      </Box>

      {/* Content Section */}
      <CardContent
        sx={{
          flexGrow: 1,
          p: isMobile ? 1.5 : 2,
          display: "flex",
          flexDirection: "column",
        }}
        onClick={() => onCarClick(car.carModelId)}
      >
        {/* Title and Basic Info */}
        <Box sx={{ mb: 1.5 }}>
          <Typography
            variant="h6"
            sx={{
              fontSize: isMobile ? "1rem" : "1.125rem",
              fontWeight: 700,
              lineHeight: 1.2,
              mb: 0.5,
              color: theme.palette.text.primary,
            }}
          >
            {car.carBrandName} {car.carModelName}
          </Typography>

          <Typography
            variant="body2"
            sx={{
              fontSize: isMobile ? "0.75rem" : "0.8125rem",
              color: theme.palette.text.secondary,
              display: "flex",
              alignItems: "center",
              gap: 0.5,
            }}
          >
            <span>{car.carCategoriesName}</span>

          </Typography>

          {/* Rating  Section */}
          {/* <Rating
            value={5}
            readOnly
            size={isMobile ? "small" : "medium"}
            sx={{
              "& .MuiRating-iconFilled": {
                color: theme.palette.warning.main,
              },
            }}
          /> */}
        </Box>

        {/* Specifications Grid */}
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "repeat(2, 1fr)",
            gap: 1,

            "& > div": {
              display: "flex",
              alignItems: "center",
              gap: 0.5,
            },
          }}
        >
          {/* Ref # */}
          <Box>
            <Typography
              variant="caption"
              sx={{
                color: theme.palette.text.secondary,
                fontSize: isMobile ? "0.65rem" : "1rem",
              }}
            >
              Ref #:
            </Typography>
            <Typography
              variant="body2"
              sx={{
                fontWeight: 500,
                fontSize: isMobile ? "0.75rem" : "0.8125rem",
              }}
            >
              {car.carRefNumber || "N/A"}
            </Typography>
          </Box>

          {/* Year */}
          <Box>
            <Typography
              variant="caption"
              sx={{
                color: theme.palette.text.secondary,
                fontSize: isMobile ? "0.65rem" : "1rem",
              }}
            >
              Year:
            </Typography>
            {car.year && <span>{car.year}</span>}
          </Box>

          {/* Engine */}
          <Box>
            <Typography
              variant="caption"
              sx={{
                color: theme.palette.text.secondary,
                fontSize: isMobile ? "0.65rem" : "1rem",
              }}
            >
              Engine:
            </Typography>
            <Typography
              variant="body2"
              sx={{
                fontWeight: 500,
                fontSize: isMobile ? "0.75rem" : "0.8125rem",
              }}
            >
              {car.carEngineValue || "N/A"}
            </Typography>
          </Box>

          {/* Fuel and Transmission Chips */}
          <Box
            sx={{
              display: "flex",
            }}
          >
            <Typography
              variant="caption"
              sx={{
                color: theme.palette.text.secondary,
                fontSize: isMobile ? "0.65rem" : "1rem",
              }}
            >
              Fuel:
            </Typography>

            {car.carFuel && (
              <Typography
                variant="body2"
                sx={{
                  fontSize: isMobile ? "0.65rem" : "0.75rem",
                  color: theme.palette.text.primary,
                }}
              >
                {car.carFuel}
              </Typography>
            )}

            {car.carTransmission && (
              <Box sx={{ display: "flex", alignItems: "center", ml: 2 }}>
                <DirectionsCar
                  fontSize={isMobile ? "small" : "medium"}
                  sx={{ color: theme.palette.primary.main, mr: 0.5 }}
                />
                <Typography
                  variant="body2"
                  sx={{
                    fontSize: isMobile ? "0.65rem" : "0.75rem",
                    color: theme.palette.text.primary,
                  }}
                >
                  {car.carTransmission}
                </Typography>
              </Box>
            )}
          </Box>

          {/* A/C */}
          {/* <Box>
            <Typography
              variant="caption"
              sx={{
                color: theme.palette.text.secondary,
                fontSize: isMobile ? "0.65rem" : "1rem",
              }}
            >
              A/C:
            </Typography>
            <Typography
              variant="body2"
              sx={{
                fontWeight: 500,
                fontSize: isMobile ? "0.75rem" : "0.8125rem",
              }}
            >
              {car.airConditioning || "N/A"}
            </Typography>
          </Box> */}

          {/* Color */}
          <Box>
            <Typography
              variant="caption"
              sx={{
                color: theme.palette.text.secondary,
                fontSize: isMobile ? "0.65rem" : "1rem",
              }}
            >
              Color:
            </Typography>
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>

              <Typography
                variant="body2"
                sx={{
                  fontWeight: 500,
                  fontSize: isMobile ? "0.75rem" : "0.8125rem",
                }}
              >
                {car.carColor || "N/A"}
              </Typography>
            </Box>
          </Box>
        </Box>

        {/* Chassis */}
        <Box sx={{ display: "flex", gap: "8px", alignItems: "baseline" }}>
          <Typography
            variant="caption"
            sx={{
              color: theme.palette.text.secondary,
              fontSize: isMobile ? "0.65rem" : "1rem",
            }}
          >
            Chassis:
          </Typography>
          <Typography
            variant="body2"
            sx={{
              fontWeight: 500,
              fontFamily: "monospace",
              // wordBreak: "break-all",
              fontSize: isMobile ? "0.75rem" : "0.8125rem",
            }}
          >
            {car.carChassisNumber || "N/A"}
          </Typography>
        </Box>

        {/* Price Section */}
        <Box sx={{ display: "flex", gap: "8px" }}>
          <Typography
            variant="caption"
            sx={{
              fontWeight: 500,
              color: theme.palette.text.secondary,
              fontSize: isMobile ? "0.65rem" : "20px",
            }}
          >
            Price:
          </Typography>
          <Typography
            variant="h6"
            sx={{
              fontSize: isMobile ? "1rem" : "20px",
              fontWeight: 700,
              color: theme.palette.error.main,
            }}
          >
            {car.carPrice
              ? `$${car.carPrice.toLocaleString()}`
              : "Price on request"}
          </Typography>
        </Box>
      </CardContent>

      {/* Action Buttons */}
      <Box
        sx={{
          p: isMobile ? 1 : 1.5,
          pt: 0,
          display: "flex",
          justifyContent: "space-between",
          gap: 0.5,
        }}
      >

        <Button
          variant="contained"
          size="medium" // Already using small size
          sx={{
            bgcolor: "transparent",
            color: "red",
            border: "1px solid red",
            borderRadius: "50px", // Smaller border radius
            fontSize: "0.75rem", // Smaller font size
            padding: "18px 18px", // Reduced padding
            minWidth: "150px", // Reduced minimum width
            height: "38px", // Explicitly set smaller height
            transition: "all 0.3s ease",
            overflow: "hidden",
            position: "relative",
            zIndex: 1,
            "&::before": {
              content: '""',
              position: "absolute",
              top: 0,
              left: 0,
              width: "100%",
              height: "100%",
              background: "red",
              transform: "translateX(-100%)",
              transition: "transform 0.3s ease",
              zIndex: -1,
            },
            "&:hover": {
              color: "white",
              boxShadow: "0 3px 6px rgba(255,0,0,0.3)", // Smaller shadow
              "&::before": {
                transform: "translateX(0)",
              },
            },
          }}
          onClick={(e) => {
            e.stopPropagation();
            setSelectedCarModelId(car.carModelId);  // Add this line
            handleModalOpen();
          }}
        >
          INQUIRY
        </Button>

        <Modal
          open={open}
          onClose={handleModalClose}
          aria-labelledby="inquiry-modal-title"
          aria-describedby="inquiry-modal-description"
        // sx={{ position: "absolute" }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              height: "100vh",
            }}
          // onClick={handleModalClose}
          >
            <Container
              maxWidth="md"
              sx={{
                position: "absolute",
                top: "50%",
                left: "50%",
                transform: "translate(-50%, -50%)",
                bgcolor: "white",
                borderRadius: "8px",
                boxShadow: 24,
                width: "90%",
                maxHeight: "90vh",
                overflow: "auto",
                p: 4,
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <Box sx={{
                display: 'flex',
                justifyContent: 'flex-end',
                position: 'absolute',
                top: 0,
                right: 8,
                zIndex: 1000
              }}>
                <IconButton
                  onClick={handleModalClose}
                >
                  <CloseIcon />
                </IconButton>
              </Box>
              {productLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '200px' }}>
                  <CircularProgress />
                </Box>
              ) : product ? (
                <Box sx={{ maxHeight: "80vh", overflowY: "auto", p: 2 }}>
                  <Typography variant="h4" fontWeight="bold" gutterBottom>
                    Get a Free Quotation
                  </Typography>
                  <Typography variant="subtitle1" gutterBottom>
                    Send us your enquiry, our sales team will contact you.
                  </Typography>

                  <Grid container spacing={4}>
                    <Grid item xs={12} md={6}>
                      <CardMedia
                        component="img"
                        height="300"
                        image={selectedCarModelId ?
                          `https://car.amar.co.jp/api/image/car-exterior-images-1/get-image/${selectedCarModelId}` :
                          "/carousel/carslider.png"
                        }
                        alt={`${product.carBrandName} ${product.carModelName}`}
                        onError={(e) => {
                          e.target.src = "/carousel/carslider.png";
                        }}
                        sx={{ borderRadius: '8px', objectFit: 'cover' }}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                        <Rating value={5} readOnly size="large" />
                        <Typography variant="body2" sx={{ ml: 2 }}>15 User Comments</Typography>
                        <IconButton sx={{ ml: 'auto' }}><Share /></IconButton>
                      </Box>

                      <Typography variant="h4" fontWeight="bold" mt={2}>
                        {`${product.brandName} ${product.modelName} ${product.carCategoriesName || product.carCategoryName || product.categoryName || ''}`}
                      </Typography>
                      <Typography variant="subtitle1" color="text.secondary" sx={{ marginTop: '12px' }}>
                        {`${product.carMileage || 'N/A'} | ${product.carTransmission || 'N/A'} | ${product.carDrive || 'N/A'} | ${product.carEngineCC || 'N/A'}`}
                      </Typography>
                      <Typography variant="body1" sx={{ marginTop: '24px' }}>
                        {product.categoriesName} with {product.carColor} exterior
                      </Typography>

                      <Box sx={{ mt: 3 }}>
                        <Typography variant="h6" color="text.secondary" sx={{ textDecoration: "line-through" }}>
                          {`$${(product.carPrice * 1.2).toFixed(2)}`}
                        </Typography>
                        <Typography variant="h4" color="error" fontWeight="bold" sx={{ marginTop: '8px' }}>
                          {`$${product.carPrice.toFixed(2)}`}
                        </Typography>
                      </Box>
                    </Grid>
                  </Grid>

                  <Typography variant="h6" fontWeight="bold" sx={{ mt: 4, mb: 2 }}>
                    Car Specifications
                  </Typography>
                  <TableContainer component={Paper}>
                    <Table>
                      <TableBody>
                        <TableRow>
                          <TableCell sx={{ bgcolor: "#f5f5f5", fontWeight: "medium" }}>
                            Reference No.
                          </TableCell>
                          <TableCell>{product?.carRefNumber || ''}</TableCell>
                          <TableCell sx={{ bgcolor: "#f5f5f5", fontWeight: "medium" }}>
                            Drive Type
                          </TableCell>
                          <TableCell>{product?.carDrive || ''}</TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell sx={{ bgcolor: "#f5f5f5", fontWeight: "medium" }}>
                            Registration Year
                          </TableCell>
                          <TableCell>{product?.year || ''}</TableCell>
                          <TableCell sx={{ bgcolor: "#f5f5f5", fontWeight: "medium" }}>
                            Model Code
                          </TableCell>
                          <TableCell>{product?.carModelCode || ''}</TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell sx={{ bgcolor: "#f5f5f5", fontWeight: "medium" }}>
                            Manufacture Year
                          </TableCell>
                          <TableCell>{product?.carManufactureDate || ''}</TableCell>
                          <TableCell sx={{ bgcolor: "#f5f5f5", fontWeight: "medium" }}>
                            Model Grade
                          </TableCell>
                          <TableCell>{product?.carGrade || ''}</TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell sx={{ bgcolor: "#f5f5f5", fontWeight: "medium" }}>
                            Mileage
                          </TableCell>
                          <TableCell>
                            {product?.carMileage || ''}
                            <br />
                            (Approx. 42,262 miles)
                          </TableCell>
                          <TableCell sx={{ bgcolor: "#f5f5f5", fontWeight: "medium" }}>
                            Transmission
                          </TableCell>
                          <TableCell>{product?.carTransmission || ''}</TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell sx={{ bgcolor: "#f5f5f5", fontWeight: "medium" }}>
                            Fuel Type
                          </TableCell>
                          <TableCell>{product?.carFuel || ''}</TableCell>
                          <TableCell sx={{ bgcolor: "#f5f5f5", fontWeight: "medium" }}>
                            Engine CC / Motor kW
                          </TableCell>
                          <TableCell>
                            {product?.carEngineCC || ''}
                            <br />
                            (0.66 liters)
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell sx={{ bgcolor: "#f5f5f5", fontWeight: "medium" }}>
                            No. of Doors
                          </TableCell>
                          <TableCell>{product?.carDoor || ''}</TableCell>
                          <TableCell sx={{ bgcolor: "#f5f5f5", fontWeight: "medium" }}>
                            No. of Seats
                          </TableCell>
                          <TableCell>{product?.carSeat || ''}</TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>

                  <Typography variant="h6" fontWeight="bold" sx={{ mt: 4, mb: 2 }}>
                    * Required fields
                  </Typography>

                  <Card sx={{
                    background: 'none',
                    border: '1px solid #ff3b30',
                    borderRadius: '16px',
                    padding: { xs: '16px', md: '32px' },
                    maxWidth: '800px',
                    margin: 'auto',
                    position: 'relative',
                    overflow: 'visible',
                    '&:before': {
                      content: '""',
                      position: 'absolute',
                      top: -2,
                      left: -2,
                      right: -2,
                      bottom: -2,
                      borderRadius: '16px',
                      zIndex: -1
                    }
                  }}>
                    <CardContent>
                      <Typography variant="h5" sx={{
                        color: '#ff3b30',
                        mb: 4,
                        textAlign: 'center',
                        textTransform: 'uppercase',
                        fontWeight: 'bold',
                        letterSpacing: '1px'
                      }}>
                        Vehicle Inquiry Form
                      </Typography>

                      {/* Inquiry Type Selection */}
                      <Grid container spacing={3} sx={{ mb: 3 }}>
                        <Grid item xs={12}>
                          <FormControl fullWidth>
                            <RadioGroup
                              row
                              value={inquiryType}
                              onChange={(e) => setInquiryType(e.target.value)}
                              sx={{
                                display: 'flex',
                                gap: 2,
                                justifyContent: 'center',
                                '@media (max-width: 600px)': {
                                  flexDirection: 'column'
                                }
                              }}
                            >
                              <FormControlLabel
                                value="general"
                                control={
                                  <Radio
                                    sx={{
                                      color: '#ff3b30',
                                      '&.Mui-checked': {
                                        color: '#ff3b30',
                                      },
                                    }}
                                  />
                                }
                                label="General Inquiry"
                                sx={{
                                  m: 0,
                                  border: '1px solid #ff3b30',
                                  borderRadius: '8px',
                                  padding: '8px 16px',
                                  '&.Mui-focused': {
                                    borderColor: '#ff3b30',
                                  },
                                }}
                              />
                              <FormControlLabel
                                value="shipping"
                                control={
                                  <Radio
                                    sx={{
                                      color: '#ff3b30',
                                      '&.Mui-checked': {
                                        color: '#ff3b30',
                                      },
                                    }}
                                  />
                                }
                                label="Request Shipping Quote"
                                sx={{
                                  m: 0,
                                  border: '1px solid #ff3b30',
                                  borderRadius: '8px',
                                  padding: '8px 16px',
                                  '&.Mui-focused': {
                                    borderColor: '#ff3b30',
                                  },
                                }}
                              />
                            </RadioGroup>
                          </FormControl>
                        </Grid>
                      </Grid>
                      {/* General Inquiry Form */}
                      {inquiryType === 'general' && (
                        <Grid container spacing={3}>
                          {/* Add refresh button at the top */}

                          {/* Personal Info Grid */}
                          <Grid item xs={12} container spacing={2}>
                            {['firstName', 'lastName', 'phoneNumber', 'emailId'].map((field) => (
                              <Grid item xs={12} md={6} key={field}>
                                <TextField
                                  fullWidth
                                  label={labelMap[field]}
                                  value={formData[field]}
                                  onChange={handleInputChangeGlobal(field)}
                                  required
                                  disabled={isLoadingUserDetails}
                                  InputLabelProps={{
                                    shrink: true
                                  }}
                                  sx={{
                                    '& .MuiOutlinedInput-root': {
                                      borderRadius: '8px',
                                      color: '#333',
                                      '& fieldset': { borderColor: '#333' },
                                      '&:hover fieldset': { borderColor: '#ff3b30' },
                                      '&.Mui-focused fieldset': { borderColor: '#ff3b30' }
                                    },
                                    '& .MuiInputLabel-root': {
                                      color: '#888',
                                      backgroundColor: '#fff',
                                      paddingX: '4px',
                                      '&.Mui-focused': {
                                        color: '#ff3b30'
                                      }
                                    }
                                  }}
                                  InputProps={{
                                    style: {
                                      backgroundColor: '#fff'
                                    }
                                  }}
                                />
                              </Grid>
                            ))}
                          </Grid>

                          {/* Message Field */}
                          <Grid item xs={12}>
                            <TextField
                              fullWidth
                              multiline
                              minRows={3}
                              label="Your Message"
                              value={formData.userMessage}
                              onChange={handleInputChange('userMessage')}
                              disabled={isLoadingUserDetails}
                              InputLabelProps={{
                                shrink: true
                              }}
                              sx={{
                                '& .MuiOutlinedInput-root': {
                                  borderRadius: '8px',
                                  color: '#333',
                                  paddingY: '12px',
                                  '& fieldset': { borderColor: '#333' },
                                  '&:hover fieldset': { borderColor: '#ff3b30' },
                                  '&.Mui-focused fieldset': { borderColor: '#ff3b30' }
                                },
                                '& .MuiInputLabel-root': {
                                  color: '#888',
                                  backgroundColor: '#fff',
                                  paddingX: '4px',
                                  '&.Mui-focused': {
                                    color: '#ff3b30'
                                  }
                                }
                              }}
                              InputProps={{
                                style: {
                                  backgroundColor: '#fff'
                                }
                              }}
                            />
                          </Grid>
                          {/* Submit Button */}
                          <Grid item xs={12} sx={{ mt: 2 }}>
                            <Button
                              fullWidth
                              variant="contained"
                              onClick={handleSubmitInquiry}
                              disabled={
                                !formData.firstName ||
                                !formData.lastName ||
                                !formData.phoneNumber ||
                                !formData.emailId ||
                                isSubmitting ||
                                isLoadingUserDetails
                              }
                              sx={{
                                background: '#ff3b30',
                                color: '#fff',
                                py: 2,
                                borderRadius: '8px',
                                fontWeight: 'bold',
                                fontSize: '1rem',
                                textTransform: 'uppercase',
                                letterSpacing: '1px',
                                transition: 'all 0.3s',
                                '&:hover': {
                                  background: '#ff5047',
                                  transform: 'translateY(-2px)',
                                  boxShadow: '0 8px 24px rgba(255, 59, 48, 0.4)'
                                }
                              }}
                            >
                              {isSubmitting ? (
                                <>
                                  <CircularProgress size={24} color="inherit" sx={{ mr: 2 }} />
                                  Submitting Inquiry...
                                </>
                              ) : (
                                'Submit Inquiry'
                              )}
                            </Button>
                          </Grid>
                        </Grid>
                      )}

                      {/* Shipping Quote Form */}
                      {inquiryType === 'shipping' && (
                        <Grid container spacing={3}>
                          {/* Country Dropdown */}
                          <Grid item xs={12} md={6}>
                            <Autocomplete
                              options={countriesList}
                              getOptionLabel={(option) => option.countryName}
                              value={formData.country}
                              onChange={(event, newValue) =>
                                handleChange({ target: { name: 'country', value: newValue } })
                              }
                              onOpen={() => {
                                if (countriesList.length === 0) fetchCountries();
                              }}
                              renderInput={(params) => (
                                <TextField
                                  {...params}
                                  label="Country"
                                  variant="outlined"

                                  InputLabelProps={{
                                    sx: { color: '#888', fontWeight: 500, shrink: true }
                                  }}
                                  sx={{
                                    '& .MuiOutlinedInput-root': {
                                      borderRadius: '8px',
                                      color: 'black !important',
                                      '& fieldset': {
                                        borderColor: '#ccc !important',
                                        borderWidth: '1px !important',
                                        borderStyle: 'solid !important'
                                      },
                                      '&:hover fieldset': {
                                        borderColor: '#ff3b30 !important',
                                        borderWidth: '1px !important',
                                        borderStyle: 'solid !important'
                                      },
                                      '&.Mui-focused fieldset': {
                                        borderColor: '#ff3b30 !important',
                                        borderWidth: '1px !important',
                                        borderStyle: 'solid !important'
                                      }
                                    }
                                  }}
                                />
                              )}
                            />
                          </Grid>
                          {/* Add rest of shipping form fields here */}
                          {/* Port Dropdown */}
                          <Grid item xs={12} md={6}>
                            <Autocomplete
                              options={portsList}
                              getOptionLabel={(option) => option.portName}
                              value={formData.port}
                              onChange={handlePortChange}
                              disabled={!formData.country}
                              onOpen={() => {
                                if (formData.country) {
                                  fetchPorts(formData.country.countryId);
                                }
                              }}
                              renderInput={(params) => (
                                <TextField
                                  {...params}
                                  label="Port"
                                  variant="outlined"
                                  InputLabelProps={{ sx: { color: '#888', fontWeight: 500, shrink: true } }}
                                  sx={{
                                    '& .MuiOutlinedInput-root': {
                                      borderRadius: '8px',
                                      color: 'black !important',
                                      '& fieldset': {
                                        borderColor: '#333 !important',
                                        borderWidth: '1px !important',
                                        borderStyle: 'solid !important'
                                      },
                                      '&:hover fieldset': {
                                        borderColor: '#ff3b30 !important',
                                        borderWidth: '1px !important',
                                        borderStyle: 'solid !important'
                                      },
                                      '&.Mui-focused fieldset': {
                                        borderColor: '#ff3b30 !important',
                                        borderWidth: '1px !important',
                                        borderStyle: 'solid !important'
                                      }
                                    }
                                  }}
                                />
                              )}
                            />
                          </Grid>

                          {/* Shipping Options - Compact Toggle Buttons */}
                          <Grid item xs={12}>
                            <FormControl fullWidth>
                              <Grid item xs={12}>
                                <Typography variant="body2" sx={{
                                  color: '#ff3b30',
                                  fontWeight: '700',
                                  fontSize: '0.95rem',
                                  mb: 2.5,
                                  position: 'relative',
                                  '&:after': {
                                    content: '""',
                                    position: 'absolute',
                                    bottom: -4,
                                    left: 0,
                                    width: '40px',
                                    height: '2px',
                                    backgroundColor: '#ff3b30'
                                  }
                                }}>
                                  Shipping Options
                                </Typography>
                              </Grid>
                              <RadioGroup
                                value={formData.shippingOption}
                                onChange={(e) => handleChange({ target: { name: 'shippingOption', value: e.target.value } })}
                                sx={{
                                  display: 'flex',
                                  gap: 2,
                                  flexDirection: 'row',
                                  '@media (max-width: 600px)': {
                                    flexDirection: 'column'
                                  }
                                }}
                                disabled={!formData.port}
                              >
                                {shippingOptions.map((option) => (
                                  <FormControlLabel
                                    key={option.value}
                                    value={option.value}
                                    control={
                                      <Radio
                                        sx={{
                                          color: '#ff3b30',
                                          '&.Mui-checked': {
                                            color: '#ff3b30',
                                          },
                                        }}
                                      />
                                    }
                                    label={
                                      <Typography variant="body2" fontWeight="500" color="#333">
                                        {option.label.split(' (')[0]}
                                      </Typography>
                                    }
                                    sx={{
                                      flex: 1,
                                      m: 0,
                                      border: '1px solid #ff3b30',
                                      borderRadius: '8px',
                                      padding: '8px 16px',
                                      '&.Mui-focused': {
                                        borderColor: '#ff3b30',
                                      },
                                      '&.Mui-selected': {
                                        backgroundColor: 'rgba(255, 59, 48, 0.1)',
                                      }
                                    }}
                                  />
                                ))}
                              </RadioGroup>
                            </FormControl>
                          </Grid>

                          {/* Fee Configuration List */}
                          <Grid item xs={12}>
                            <Typography variant="body2" sx={{
                              color: '#ff3b30',
                              fontWeight: '700',
                              fontSize: '0.95rem',
                              mb: 0.5,
                              position: 'relative',
                              '&:after': {
                                content: '""',
                                position: 'absolute',
                                bottom: -4,
                                left: 0,
                                width: '40px',
                                height: '2px',
                                backgroundColor: '#ff3b30'
                              }
                            }}>
                              Fee Configuration List
                            </Typography>
                          </Grid>

                          {/* Fee Configuration Section */}
                          <Grid item xs={12} sx={{ mb: 3 }}>
                            <Box sx={{
                              backgroundColor: '#f8f9fa',
                              borderRadius: '12px',
                              p: 2.5,
                              boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
                              border: '1px solid #e0e0e0'
                            }}>
                              <Typography variant="subtitle1" sx={{
                                color: '#333',
                                fontWeight: '600',
                                mb: 2,
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1
                              }}>
                                <ReceiptIcon fontSize="small" />
                                FEE BREAKDOWN
                              </Typography>
                              {/* Fee Configuration Grid */}
                              <Grid container spacing={1.5}>

                                {feeConfigurations.map(({ key, label, apiKey, isBaseCost }) => (
                                  <Grid item xs={12} sm={6} md={4} key={key}>
                                    <Box sx={{

                                      display: 'flex',

                                      justifyContent: 'space-between',

                                      alignItems: 'center',

                                      backgroundColor: isBaseCost ? 'rgba(33, 150, 243, 0.08)' : 'white',

                                      borderRadius: '8px',

                                      p: '10px 15px',

                                      boxShadow: '0 1px 3px rgba(0,0,0,0.05)',

                                      borderLeft: `3px solid ${isBaseCost ? '#2196f3' : '#ff3b30'}`,

                                      height: '100%',

                                      transition: 'all 0.2s ease',

                                      '&:hover': {

                                        transform: 'translateY(-2px)',

                                        boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1)'

                                      }

                                    }}>
                                      <Box>
                                        <Typography variant="caption" sx={{

                                          color: isBaseCost ? '#1976d2' : '#666',

                                          fontWeight: '500',

                                          fontSize: '0.7rem',

                                          display: 'block'

                                        }}>

                                          {label}
                                        </Typography>
                                        <Typography variant="body2" sx={{

                                          color: isBaseCost ? '#1976d2' : '#333',

                                          fontWeight: '600',

                                          fontSize: '0.9rem',

                                          letterSpacing: '-0.3px'

                                        }}>

                                          ${shippingCostData?.charges[apiKey].toFixed(2) || '0.00'}
                                        </Typography>
                                      </Box>
                                      <InfoOutlinedIcon sx={{

                                        color: isBaseCost ? '#64b5f6' : '#999',

                                        fontSize: '1rem',

                                        '&:hover': {

                                          color: isBaseCost ? '#1976d2' : '#ff3b30',

                                          cursor: 'pointer'

                                        }

                                      }} />
                                    </Box>
                                  </Grid>

                                ))}
                              </Grid>

                              {/* Subtotal of Fees */}
                              <Box sx={{

                                mt: 2,

                                p: 2,

                                backgroundColor: 'rgba(255, 59, 48, 0.05)',

                                borderRadius: '8px',

                                border: '1px dashed rgba(255, 59, 48, 0.3)',

                                display: 'flex',

                                justifyContent: 'space-between',

                                alignItems: 'center'

                              }}>
                                <Typography variant="body1" sx={{

                                  fontWeight: '600',

                                  color: '#333'

                                }}>

                                  Subtotal Fees
                                </Typography>
                                <Typography variant="body1" sx={{

                                  fontWeight: '700',

                                  color: '#ff3b30'

                                }}>

                                  ${feeConfigurations

                                    .filter(fee => !fee.isBaseCost)  // Exclude base cost

                                    .reduce((total, { apiKey }) =>

                                      total + (shippingCostData?.charges[apiKey] || 0), 0).toFixed(2)}
                                </Typography>
                              </Box>


                            </Box>
                          </Grid>

                          {/* Total Cost Section */}
                          <Grid item xs={12}>
                            <Box sx={{
                              border: '1px solid #e0e0e0',
                              borderRadius: '12px',
                              p: 3,
                              backgroundColor: 'white',
                              boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
                              position: 'relative',
                              overflow: 'hidden',
                              '&:before': {
                                content: '""',
                                position: 'absolute',
                                top: 0,
                                left: 0,
                                width: '5px',
                                height: '100%',
                                backgroundColor: '#ff3b30'
                              }
                            }}>
                              <Typography variant="h6" sx={{
                                color: '#333',
                                fontWeight: '700',
                                fontSize: '1.1rem',
                                mb: 3,
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1.5
                              }}>
                                <CalculateIcon sx={{ color: '#ff3b30', fontSize: '1.4rem' }} />
                                TOTAL COST SUMMARY
                              </Typography>

                              {/* Cost Breakdown */}
                              <Grid container spacing={2} sx={{ mb: 2 }}>
                                {[

                                  {

                                    label: 'Vehicle Price',

                                    value: product?.carPrice.toFixed(2) || '0.00',

                                    icon: <DirectionsCarIcon sx={{ color: '#ff3b30' }} />,

                                    tooltip: 'Base price of the vehicle before any fees'

                                  },

                                  {

                                    label: 'm³ Charge',

                                    value: (shippingCostData?.charges.baseShippingCost || 0).toFixed(2),

                                    icon: <StraightenIcon sx={{ color: '#2196f3' }} />,

                                    tooltip: 'Base shipping cost based on vehicle volume',

                                    color: '#1976d2'

                                  },

                                  {

                                    label: 'Port Charges',

                                    value: feeConfigurations

                                      .filter(fee => !fee.isBaseCost)

                                      .reduce((total, { apiKey }) =>

                                        total + (shippingCostData?.charges[apiKey] || 0), 0).toFixed(2),

                                    icon: <LocalShippingIcon sx={{ color: '#ff3b30' }} />,

                                    tooltip: 'Total of all port charges and additional fees'

                                  },

                                  {

                                    label: 'Estimated Total',

                                    value: (shippingCostData?.charges.totalCost ?? product?.carPrice)?.toFixed(2) || '0.00',

                                    icon: <AttachMoneyIcon sx={{ color: '#ff3b30' }} />,

                                    tooltip: 'Final estimated amount including all costs'

                                  }

                                ].map((field) => (
                                  <Grid item xs={12} md={3} key={field.label}>
                                    <Tooltip title={field.tooltip} arrow>
                                      <Box sx={{

                                        backgroundColor: 'rgba(255,255,255,0.95)',

                                        borderRadius: '8px',

                                        p: 2,

                                        border: '1px solid rgba(0, 0, 0, 0.08)',

                                        height: '100%',

                                        transition: 'all 0.3s ease',

                                        '&:hover': {

                                          transform: 'translateY(-3px)',

                                          boxShadow: '0 8px 16px rgba(0, 0, 0, 0.1)'

                                        }

                                      }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>

                                          {field.icon}
                                          <Typography variant="body2" sx={{

                                            color: field.color || '#666',

                                            fontWeight: '600',

                                            fontSize: '0.9rem'

                                          }}>

                                            {field.label}
                                          </Typography>
                                        </Box>
                                        <Typography variant="h6" sx={{

                                          color: field.label === 'Estimated Total' ? '#ff3b30' : (field.color || '#333'),

                                          fontWeight: field.label === 'Estimated Total' ? '800' : '700',

                                          fontSize: '1.3rem',

                                          pl: 3.5

                                        }}>

                                          ${field.value}
                                        </Typography>
                                      </Box>
                                    </Tooltip>
                                  </Grid>

                                ))}


                              </Grid>

                              {/* Payment Note */}
                              <Box sx={{
                                mt: 2,
                                p: 2,
                                backgroundColor: 'rgba(255, 235, 59, 0.15)',
                                borderRadius: '8px',
                                borderLeft: '3px solid #ffc107'
                              }}>
                                <Typography variant="body2" sx={{
                                  color: '#666',
                                  fontWeight: '500',
                                  display: 'flex',
                                  alignItems: 'flex-start',
                                  gap: 1
                                }}>
                                  <InfoOutlinedIcon fontSize="small" sx={{ color: '#ffc107', mt: '2px' }} />
                                  Final amount may vary based on currency exchange rates at time of payment. A 10% deposit is required to secure your vehicle.
                                </Typography>
                              </Box>
                            </Box>
                          </Grid>

                          {/* Personal Info Grid */}
                          <Grid item xs={12} container spacing={2}>
                            {['firstName', 'lastName', 'phoneNumber', 'emailId'].map((field) => (
                              <Grid item xs={12} md={6} key={field}>
                                <TextField
                                  fullWidth
                                  label={labelMap[field]}
                                  value={formData[field]}
                                  onChange={handleInputChange(field)}
                                  required
                                  InputLabelProps={{
                                    shrink: true
                                  }}
                                  sx={{
                                    '& .MuiOutlinedInput-root': {
                                      borderRadius: '8px',
                                      color: '#333',
                                      '& fieldset': { borderColor: '#333' },
                                      '&:hover fieldset': { borderColor: '#ff3b30' },
                                      '&.Mui-focused fieldset': { borderColor: '#ff3b30' }
                                    },
                                    '& .MuiInputLabel-root': {
                                      color: '#888',
                                      '&.Mui-focused': {
                                        color: '#ff3b30'
                                      }
                                    }
                                  }}
                                  InputProps={{
                                    style: {
                                      backgroundColor: '#fff'
                                    }
                                  }}
                                />
                              </Grid>
                            ))}
                          </Grid>

                          {/* Message Field */}
                          <Grid item xs={12}>
                            <TextField
                              fullWidth
                              multiline
                              minRows={3}
                              label="Your Message"
                              value={formData.userMessage}
                              onChange={handleInputChange('userMessage')}
                              InputLabelProps={{
                                shrink: true
                              }}
                              sx={{
                                '& .MuiOutlinedInput-root': {
                                  borderRadius: '8px',
                                  color: '#333',
                                  '& fieldset': { borderColor: '#333' },
                                  '&:hover fieldset': { borderColor: '#ff3b30' }
                                },
                                '& .MuiInputLabel-root': { color: '#888' }
                              }}
                            />
                          </Grid>

                          {/* Submit Button */}
                          <Grid item xs={12} sx={{ mt: 2 }}>
                            <Button
                              fullWidth
                              variant="contained"
                              onClick={handleSubmitInquiry}
                              disabled={!isPortSelected || isSubmitting}
                              sx={{
                                background: '#ff3b30',
                                color: '#fff',
                                py: 2,
                                borderRadius: '8px',
                                fontWeight: 'bold',
                                fontSize: '1rem',
                                textTransform: 'uppercase',
                                letterSpacing: '1px',
                                transition: 'all 0.3s',
                                '&:hover': {
                                  background: '#ff5047',
                                  transform: 'translateY(-2px)',
                                  boxShadow: '0 8px 24px rgba(255, 59, 48, 0.4)'
                                }
                              }}
                            >
                              {isSubmitting ? (
                                <>
                                  <CircularProgress size={24} color="inherit" sx={{ mr: 2 }} />
                                  Submitting Inquiry...
                                </>
                              ) : (
                                'Submit Inquiry'
                              )}
                            </Button>
                          </Grid>
                        </Grid>
                      )}

                      <Snackbar
                        open={showSuccessPopup}
                        autoHideDuration={2000}
                        onClose={() => {
                          setShowSuccessPopup(false);
                          handleModalClose();
                        }}
                        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
                      >
                        <Alert
                          onClose={() => {
                            setShowSuccessPopup(false);
                            handleModalClose();
                          }}
                          severity="success"
                          sx={{ width: '100%' }}
                        >
                          Inquiry sent successfully!
                        </Alert>
                      </Snackbar>

                      {/* Error Snackbar */}
                      <Snackbar
                        open={showErrorPopup}
                        autoHideDuration={6000}
                        onClose={() => setShowErrorPopup(false)}
                        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
                      >
                        <Alert
                          onClose={() => setShowErrorPopup(false)}
                          severity="error"
                          sx={{ width: '100%' }}
                        >
                          Failed to submit inquiry. Please try again.
                        </Alert>
                      </Snackbar>
                    </CardContent>
                  </Card>
                </Box>
              ) : (
                <Typography>Product not found</Typography>
              )}
            </Container>
          </Box>
        </Modal>
        {/* <Button
          variant="contained"
          size="small" // Already using small size
          sx={{
            bgcolor: "red",
            color: "white",
            borderRadius: "50px", // Smaller border radius
            fontSize: "0.75rem", // Smaller font size
            padding: "18px 18px", // Reduced padding
            minWidth: "150px", // Reduced minimum width
            height: "38px", // Explicitly set smaller height
            position: "relative",
            overflow: "hidden",
            transition: "all 0.3s ease",
            "&:disabled": {
              bgcolor: "#cccccc",
              cursor: "not-allowed",
            },
            "&::after": {
              content: '""',
              position: "absolute",
              width: "30px",
              height: "100%",
              top: 0,
              left: "-50px",
              backgroundColor: "rgba(255,255,255,0.5)",
              transform: "skewX(-25deg)",
              transition: "all 0.4s ease",
            },
            "&:hover": {
              bgcolor: "#c00",
              boxShadow: "0 4px 10px rgba(255,0,0,0.3)", // Smaller shadow
              "&::after": {
                left: "120%",
              },
            },
          }}
          disabled={cartItems.some(
            (item) => item.modelId === car.carModelId
          )}
          onClick={(e) => {
            e.stopPropagation();
            if (isFavorite) {
              setDialogType("move");
              setSelectedModelId(car.carModelId);
              setOpenDialog(true);
            } else {
              onAddToCart(car.carModelId);
            }
          }}
        >
          {isFavorite
            ? "MOVE TO CART"
            : cartItems.some(
              (item) => item.modelId === car.carModelId
            )
              ? "ADDED"
              : "ADD TO CART"}
        </Button> */}

      </Box>

      {/* Confirmation Dialog (keep existing dialog code) */}
      <Dialog
        open={openDialog}
        onClose={() => setOpenDialog(false)}
        PaperProps={{
          sx: {
            borderRadius: "12px",
            boxShadow: "0 8px 32px rgba(0,0,0,0.2)",
          },
        }}
      >
        <DialogTitle
          sx={{
            fontWeight: 600,
            borderBottom: `1px solid ${theme.palette.divider}`,
            py: 2,
          }}
        >
          {dialogType === "move"
            ? "Confirm Move to Cart"
            : "Remove from Wishlist"}
        </DialogTitle>
        <DialogContent sx={{ py: 3 }}>
          <DialogContentText>
            {dialogType === "move"
              ? "Are you sure you want to move this item to your cart?"
              : "Remove this item from your wishlist?"}
          </DialogContentText>
        </DialogContent>
        <DialogActions
          sx={{
            px: 3,
            py: 2,
            borderTop: `1px solid ${theme.palette.divider}`,
          }}
        >
          <Button
            onClick={() => setOpenDialog(false)}
            sx={{
              color: theme.palette.text.secondary,
              "&:hover": {
                backgroundColor: theme.palette.action.hover,
              },
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={
              dialogType === "move" ? handleConfirmMove : handleConfirmRemove
            }
            variant="contained"
            color="primary"
            sx={{
              boxShadow: "none",
              "&:hover": {
                boxShadow: "none",
                backgroundColor: theme.palette.primary.dark,
              },
            }}
          >
            Confirm
          </Button>
        </DialogActions>
      </Dialog>
    </StyledCard>
  );
};

const CarListingTable = ({
  cars,
  onCarClick,
  onAddToWishlist,
  onAddToCart,
  imageUrl,
}) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const cartItems = useSelector((state) => state.cart.items);
  const { enqueueSnackbar } = useSnackbar();
  const wishlistItems = useSelector((state) => state.wishlist.items);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isTablet = useMediaQuery(theme.breakpoints.between("sm", "md"));
  const visibleColumns = isMobile ? 3 : isTablet ? 6 : 11;

  const [openDialog, setOpenDialog] = useState(false);
  const [dialogType, setDialogType] = useState("");
  const [selectedModelId, setSelectedModelId] = useState(null);
  const [isHovered, setIsHovered] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [open, setOpen] = useState(false);
  const [product, setProduct] = useState(null);
  const { modelId } = useParams();
  const [imageError, setImageError] = useState({});
  const [showErrorPopup, setShowErrorPopup] = useState(false);
  const [inquiryType, setInquiryType] = useState('general');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccessPopup, setShowSuccessPopup] = useState(false);
  const [isPortSelected, setIsPortSelected] = useState(false);
  const [shippingCostData, setShippingCostData] = useState(null);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [currentImageType, setCurrentImageType] = useState('exterior');
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalImageIndex, setModalImageIndex] = useState(0);
  const [showDetails, setShowDetails] = useState(false);
  const [productLoading, setProductLoading] = useState(true);
  const [selectedCarModelId, setSelectedCarModelId] = useState(null);
  const [userDetails, setUserDetails] = useState(null);

  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    emailId: "",
    phoneNumber: "",
    description: "",
    country: null,
    port: null,
    userMessage: '',
    shippingOption: ''
  });


  const [countriesList, setCountriesList] = useState([]);
  const [portsList, setPortsList] = useState([]);
  const handlePortChange = (event, newValue) => {
    handleChange({ target: { name: 'port', value: newValue } });
    setIsPortSelected(!!newValue); // Enable button only when port is selected
  };



  // <------------------ user details api integration code start ---------------->

  const [isLoadingUserDetails, setIsLoadingUserDetails] = useState(false);

  // Add this function to fetch user details
  const fetchUserDetailsGlobal = async () => {
    try {
      // Get the token from localStorage or wherever you store it
      const token = localStorage.getItem('authToken') || 'eyJhbGciOiJIUzUxMiJ9.eyJzdWIiOiJoYXJkaWtAZ3JldHRvbS5jb20iLCJleHAiOjE3NTQ4NzY2NjEsImlhdCI6MTc1MTI3NjY2MSwiYXV0aG9yaXRpZXMiOiIifQ.caR99Ex3qtHfp9tLCMbsDxf9uxt_KhK9IlDbrB9-rek498PHySU1-FQhmBmTtK947DnUBXKS6CwNy6PccCH-4A';

      const response = await fetch('https://car.amar.co.jp/api/authorization/user/get/user/details', {
        method: 'GET',
        headers: {
          'accept': 'application/json',
          'Accept-Language': 'en',
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const userData = await response.json();
      return userData;

    } catch (error) {
      console.error('Error fetching user details:', error);
      throw error;
    }
  };

  // Add this useEffect to populate form data when component mounts
  useEffect(() => {
    const loadUserDetails = async () => {
      try {
        setIsLoadingUserDetails(true);
        const response = await fetchUserDetailsGlobal();
        const userDetails = response.userDetails;

        // Map API response to your form fields
        // Adjust the field names based on your API response structure
        setFormData(prevData => ({
          ...prevData,
          firstName: userDetails.firstName || '',
          lastName: userDetails.lastName || '',
          phoneNumber: userDetails.userMobileNumber || '',
          emailId: userDetails.email || ''
        }));

      } catch (error) {
        console.error('Failed to load user details:', error);
        // Optionally show error message to user
      } finally {
        setIsLoadingUserDetails(false);
      }
    };

    // Only fetch if inquiryType is 'general' and form is empty
    if (inquiryType === 'general' && !formData.firstName) {
      loadUserDetails();
    }
  }, [inquiryType]); // Run when inquiryType changes

  // Your existing handleInputChange function
  const handleInputChangeGlobal = (field) => (event) => {
    setFormData(prev => ({
      ...prev,
      [field]: event.target.value
    }));
  };

  // Add this function to manually refresh user details
  const handleRefreshUserDetails = async () => {
    try {
      setIsSubmitting(true);
      const userDetails = await fetchUserDetailsGlobal();

      setFormData(prevData => ({
        ...prevData,
        firstName: userDetails.firstName || userDetails.first_name || '',
        lastName: userDetails.lastName || userDetails.last_name || '',
        phoneNumber: userDetails.phoneNumber || userDetails.phone || userDetails.mobile || '',
        emailId: userDetails.emailId || userDetails.email || userDetails.emailAddress || ''
      }));

      console.log('User details loaded successfully');

    } catch (error) {
      console.error('Failed to refresh user details:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  // <------------------ user details api integration code end ---------------->


  // Styles object with improved spacing and alignment
  const styles = {
    globalWrapper: {
      width: '100%',
      maxWidth: '100%',
      padding: 0,
    },
  };

  const currentModelId = modelId || cars?.carModelId;
  const countries = ["AUSTRALIA", "ARUBA", "BAHAMAS", "BERMUDA", "CANADA", "CHILE", "DENMARK", "DOMINICA", "ETHIOPIA", "FIJI"];
  const shippingOptions = [
    { value: "CIF", label: "CIF (Cost, Insurance and Freight)" },
    { value: "CNF", label: "C&F (Cost and Freight)" },
    { value: "FOB", label: "FOB (Free On Board)" }
  ];
  const feeConfigurations = [
    { key: 'INSURANCE', label: 'Insurance', apiKey: 'insuranceCost' },
    { key: 'DOCUMENTATION', label: 'Documentation', apiKey: 'documentationFee' },
    { key: 'CURRENCY_ADJUSTMENT_FACTOR', label: 'Currency Adjustment', apiKey: 'currencyAdjustmentFactor' },
    { key: 'BUNKER_ADJUSTMENT_FACTOR', label: 'Bunker Adjustment', apiKey: 'bunkerAdjustmentFactor' },
    { key: 'TERMINAL_HANDLING', label: 'Terminal Handling', apiKey: 'terminalHandlingCharge' },
    { key: 'CUSTOMS_CLEARANCE', label: 'Customs Clearance', apiKey: 'customsClearanceFee' },
    { key: 'baseCostPerM3', label: 'Base Cost per m³', apiKey: 'baseShippingCost', isBaseCost: true }
  ];

  const labelMap = {
    firstName: "First Name *",
    lastName: "Last Name *",
    phoneNumber: "Phone Number *",
    emailId: "Email Address *"
  };

  // Fetch countries when Country Dropdown is opened
  const fetchCountries = async () => {
    try {
      const response = await fetch('https://car.amar.co.jp/api/authorization/shipping/countries');
      const data = await response.json();
      setCountriesList(data);
    } catch (error) {
      console.error('Error fetching countries:', error);
    }
  };

  // Fetch ports when Port Dropdown is opened
  const fetchPorts = async (countryId) => {
    try {
      const response = await fetch(`https://car.amar.co.jp/api/authorization/shipping/ports/country/${countryId}`);
      const data = await response.json();
      setPortsList(data);
    } catch (error) {
      console.error('Error fetching ports:', error);
    }
  };

  const handleChange = (event) => {
    const { name, value } = event.target;
    setFormData(prev => ({
      ...prev,
      [name]: value,
      ...(name === 'country' && { port: null }) // Reset port when country changes
    }));
  };

  const fetchUserDetails = async (token) => {
    try {
      const response = await axios.get('https://car.amar.co.jp/api/authorization/user/details', {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      setUserDetails(response.data.data);
    } catch (error) {
      console.error('Failed to fetch user details:', error);
      // If token is invalid, remove it
      localStorage.removeItem('token');
      setUserDetails(null);
    }
  };

  // First, check token when component mounts
  useEffect(() => {
    const token = localStorage.getItem("authToken");
    if (token) {
      fetchUserDetails(token).catch(error => {
        console.error('Failed to fetch user details dddd:', error);
        localStorage.removeItem('token'); // Remove invalid token
      });
    }
  }, []);

  // Send general inquiry with token
  const sendGeneralInquiry = async (token) => {
    const headers = {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    };

    const response = await fetch('https://car.amar.co.jp/api/dashboard/inquiry/send-inquiry', {
      method: 'POST',
      headers,
      body: JSON.stringify({
        firstName: formData.firstName,
        lastName: formData.lastName,
        emailId: formData.emailId,
        phoneNumber: formData.phoneNumber,
        description: formData.userMessage,
        modelId: parseInt(selectedCarModelId, 10) // Add modelId from URL params
      })
    });

    if (!response.ok) throw new Error('Inquiry failed');
  };

  // Send shipping inquiry with token
  const sendShippingInquiry = async (token) => {
    const headers = {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    };

    const payload = {
      carModelId: parseInt(selectedCarModelId, 10), // Changed from modelId to selectedCarModelId
      portId: formData.port?.portId,
      shippingOption: formData.shippingOption,
      isSendInquiry: true,
      firstName: formData.firstName,
      lastName: formData.lastName,
      userMobileNumber: formData.phoneNumber,
      userMessage: formData.userMessage,
      userEmailId: formData.emailId
    };

    const response = await fetch('https://car.amar.co.jp/api/authorization/shipping/calculate', {
      method: 'POST',
      headers,
      body: JSON.stringify(payload)
    });

    if (!response.ok) throw new Error('Shipping inquiry failed');
  };

  const handleInputChange = (field) => (e) => {
    setFormData({
      ...formData,
      [field]: e.target.value
    });
  };

  // Handle form submission with token verification
  const handleSubmitInquiry = async () => {
    const token = localStorage.getItem("authToken");
    if (!token) {
      alert('Please login to submit inquiry');
      return;
    }

    setIsSubmitting(true);

    try {
      if (inquiryType === 'general') {
        await sendGeneralInquiry(token);
      } else {
        await sendShippingInquiry(token);
      }
      setShowSuccessPopup(true);
    } catch (error) {
      setShowErrorPopup(true);
    } finally {
      setIsSubmitting(false);
    }
  };

  useEffect(() => {
    const calculateShippingCost = async () => {
      if (selectedCarModelId && formData.port?.portId && formData.shippingOption) { // Changed from modelId to selectedCarModelId
        try {
          const response = await fetch('https://car.amar.co.jp/api/authorization/shipping/calculate', {
            method: 'POST',
            headers: {
              'accept': 'application/json',
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              carModelId: parseInt(selectedCarModelId, 10), // Changed from modelId to selectedCarModelId
              portId: formData.port.portId,
              shippingOption: formData.shippingOption,
              isSendInquiry: false
            })
          });
          const data = await response.json();
          setShippingCostData(data);
        } catch (error) {
          console.error('Error calculating shipping cost:', error);
        }
      }
    };

    calculateShippingCost();
  }, [selectedCarModelId, formData.port, formData.shippingOption]);

  const handleModalOpen = () => setOpen(true);
  const handleModalClose = () => {
    setOpen(false);
    setSelectedCarModelId(null); // Reset the selected ID
  };

  useEffect(() => {
    const fetchProductData = async () => {
      const currentModelId = selectedCarModelId || modelId;
      if (!currentModelId) return;

      try {
        setProductLoading(true);
        const response = await axios.get(`https://car.amar.co.jp/api/dashboard/product/get/Model/detail/${currentModelId}`);
        setProduct(response.data.data);
        setProductLoading(false);
      } catch (err) {
        setError('Failed to fetch product data. Please try again later.');
        setProductLoading(false);
        console.error('Error fetching product data:', err);
      }
    };

    fetchProductData();
  }, [modelId, selectedCarModelId]);




  const handleFavoriteClick = async (e, carModelId, isFavorite) => {
    e.stopPropagation();
    setSelectedModelId(carModelId);

    if (isFavorite) {
      // Show removal confirmation dialog
      setDialogType("remove");
      setOpenDialog(true);
    } else {
      try {
        const token = localStorage.getItem("authToken");
        if (!token) {
          navigate("/login");
          return;
        }
        const result = await dispatch(addToWishlist(carModelId));
        if (result.success) {
          enqueueSnackbar("Added to wishlist successfully", {
            variant: "success",
          });
        } else if (result.alreadyInWishlist) {
          enqueueSnackbar("This item is already in your wishlist", {
            variant: "info",
          });
        } else {
          enqueueSnackbar(result.message || "Failed to add to wishlist", {
            variant: "error",
          });
        }
      } catch (error) {
        enqueueSnackbar("Error adding to wishlist", { variant: "error" });
        console.error("Error adding to wishlist:", error);
      }
    }
  };

  const handleConfirmAction = async () => {
    try {
      const token = localStorage.getItem("authToken");
      if (!token) {
        navigate("/login");
        return;
      }

      if (dialogType === "remove") {
        // DELETE API call for removing from wishlist
        const response = await fetch(
          `https://car.amar.co.jp/api/master/car-cart-and-whishlist/delete/item?Enter%20ModelId=${selectedModelId}`,
          {
            method: "DELETE",
            headers: {
              accept: "application/json",
              "Accept-Language": "en",
              Authorization: `Bearer ${token}`,
            },
          }
        );

        if (response.ok) {
          enqueueSnackbar("Removed from wishlist successfully", {
            variant: "success",
          });
          dispatch(fetchWishlist());
        } else {
          const data = await response.json();
          enqueueSnackbar(data.message || "Failed to remove from wishlist", {
            variant: "error",
          });
        }
      } else if (dialogType === "move") {
        // POST API call for moving to cart
        const response = await fetch(
          `https://car.amar.co.jp/api/master/car-cart-and-whishlist/manage/item?modelId=${selectedModelId}&SelectType=MOVE_TO_CART`,
          {
            method: "POST",
            headers: {
              accept: "application/json",
              "Accept-Language": "en",
              Authorization: `Bearer ${token}`,
            },
            body: "",
          }
        );
        if (response.ok) {
          enqueueSnackbar("Item moved to cart successfully", {
            variant: "success",
          });
          dispatch(fetchWishlist());
          dispatch(fetchCart());
        } else {
          const data = await response.json();
          enqueueSnackbar(data.message || "Failed to move item to cart", {
            variant: "error",
          });
        }
      }
    } catch (error) {
      enqueueSnackbar("Error processing request", { variant: "error" });
      console.error("Error:", error);
    } finally {
      setOpenDialog(false);
      setSelectedModelId(null);
      setDialogType("");
    }
  };

  return (
    <StyledTableContainer component={Paper}>
      <Table stickyHeader sx={{ minWidth: isMobile ? "650px" : "800px" }}>
        <TableHead>
          <TableRow>
            <StyledTableHeaderCell align="center">
              Drive This Beauty
            </StyledTableHeaderCell>
            {visibleColumns > 1 && (
              <StyledTableHeaderCell align="center">
                Highlights
              </StyledTableHeaderCell>
            )}
            {visibleColumns > 2 && (
              <StyledTableHeaderCell align="center">
                Ref #
              </StyledTableHeaderCell>
            )}
            {visibleColumns > 3 && (
              <StyledTableHeaderCell align="center">Year</StyledTableHeaderCell>
            )}
            {visibleColumns > 4 && (
              <StyledTableHeaderCell align="center">
                Engine
              </StyledTableHeaderCell>
            )}
            {visibleColumns > 5 && (
              <StyledTableHeaderCell align="center">Fuel</StyledTableHeaderCell>
            )}
            {/* {visibleColumns > 6 && (
              <StyledTableHeaderCell align="center">A/C</StyledTableHeaderCell>
            )} */}
            {visibleColumns > 7 && (
              <StyledTableHeaderCell align="center">
                Color
              </StyledTableHeaderCell>
            )}
            {visibleColumns > 8 && (
              <StyledTableHeaderCell align="center">
                Chassis
              </StyledTableHeaderCell>
            )}
            {visibleColumns > 9 && (
              <StyledTableHeaderCell align="center">
                Price
              </StyledTableHeaderCell>
            )}
            <StyledTableHeaderCell align="center">
              Actions
            </StyledTableHeaderCell>
          </TableRow>
        </TableHead>

        <TableBody>
          {cars.map((car) => {
            const isFavorite = wishlistItems.some(
              (item) => item.modelId === car.carModelId
            );
            const carImageUrl = imageUrl[car.carModelId] || "/carousel/carslider.png";

            return (
              <StyledTableRow
                key={car.carModelId}
                onClick={() => onCarClick(car.carModelId)}
              >
                <TableCell
                  sx={{
                    px: { xs: 1, sm: 2 },
                    maxWidth: isMobile ? "120px" : "unset",
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: isMobile ? 1 : 2,
                    }}
                  >
                    <Box
                      sx={{
                        position: "relative",
                        width: isMobile ? 60 : 80,
                        height: isMobile ? 45 : 60,
                      }}
                    >
                      <Box
                        component="img"
                        src={carImageUrl}
                        alt={`${car.carBrandName} ${car.carModelName}`}
                        sx={{
                          width: "100%",
                          height: "100%",
                          objectFit: "cover",
                          borderRadius: 1,
                          boxShadow: "0 4px 8px rgba(0,0,0,0.1)",
                        }}
                      />
                      <IconButton
                        size="small"
                        color="error"
                        onClick={(e) =>
                          handleFavoriteClick(e, car.carModelId, isFavorite)
                        }
                        sx={{
                          position: "absolute",
                          top: 2,
                          right: 2,
                          padding: isMobile ? "2px" : "3px",
                          backgroundColor: "rgba(255,255,255,0.8)",
                        }}
                      >
                        {isFavorite ? (
                          <Favorite sx={{ fontSize: isMobile ? 10 : 14 }} />
                        ) : (
                          <FavoriteBorder
                            sx={{ fontSize: isMobile ? 10 : 14 }}
                          />
                        )}
                      </IconButton>
                    </Box>
                    <Box>
                      <Typography
                        variant="subtitle1"
                        sx={{
                          fontWeight: "bold",
                          fontSize: isMobile ? "0.75rem" : "0.875rem",
                          textOverflow: "ellipsis",
                          overflow: "hidden",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {car.carBrandName} {car.carModelName}
                      </Typography>
                      {/* <Rating
                        value={5}
                        readOnly
                        size="small"
                        sx={{ fontSize: isMobile ? "0.6rem" : "0.75rem" }}
                      /> */}
                    </Box>
                  </Box>
                </TableCell>

                {visibleColumns > 1 && (
                  <TableCell sx={{ padding: isMobile ? "4px" : "8px" }}>
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        gap: 0.5,
                      }}
                    >
                      {car.ourPick && (
                        <Chip
                          label="Our Pick"
                          size="small"
                          sx={{
                            fontSize: isMobile ? "0.55rem" : "0.65rem",
                            height: isMobile ? "16px" : "18px",
                            color: "#FF69B4", // Neon pink
                            backgroundColor: "white",
                            fontWeight: "bold",
                            border: "1px solid #FF69B4",
                            "&:hover": { backgroundColor: "#4A0026" },
                          }}
                        />
                      )}
                      <Chip
                        label={`Grade ${car.carGrade || "A"}`}
                        size="small"
                        sx={{
                          fontSize: isMobile ? "0.55rem" : "0.65rem",
                          height: isMobile ? "16px" : "18px",
                          color: "#1565c0", // Neon mint
                          backgroundColor: "white",
                          fontWeight: "bold",
                          border: "1px solid #1565c0",
                          "&:hover": { backgroundColor: "#00382A" },
                        }}
                      />
                      {car.specialDeal && (
                        <Chip
                          label="Special Offer"
                          size="small"
                          sx={{
                            fontSize: isMobile ? "0.55rem" : "0.65rem",
                            height: isMobile ? "16px" : "18px",
                            backgroundColor: "white",
                            color: "#9D70FF", // Periwinkle
                            fontWeight: "bold",
                            border: "1px solid #9D70FF",
                            "&:hover": { backgroundColor: "#26004A" },
                          }}
                        />
                      )}
                    </Box>
                  </TableCell>
                )}

                {visibleColumns > 2 && (
                  <TableCell
                    align="center"
                    sx={{ padding: isMobile ? "4px" : "8px" }}
                  >
                    <Typography
                      sx={{
                        fontSize: isMobile ? "0.7rem" : "0.875rem",
                        fontFamily: "monospace",
                      }}
                    >
                      {car.carRefNumber || "N/A"}
                    </Typography>
                  </TableCell>
                )}

                {visibleColumns > 3 && (
                  <TableCell
                    align="center"
                    sx={{ padding: isMobile ? "4px" : "8px" }}
                  >
                    <Typography
                      sx={{ fontSize: isMobile ? "0.7rem" : "0.875rem" }}
                    >
                      {car.year || "N/A"}
                    </Typography>
                  </TableCell>
                )}

                {visibleColumns > 4 && (
                  <TableCell
                    align="center"
                    sx={{ padding: isMobile ? "4px" : "8px" }}
                  >
                    <Typography
                      sx={{
                        fontSize: isMobile ? "0.7rem" : "0.875rem",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        gap: "4px",
                      }}
                    >
                      <img
                        src="/BrandIcons/engine.svg"
                        alt="Engine"
                        sx={{ fontSize: isMobile ? "0.9rem" : "1rem" }}
                      />
                      {car.carEngineValue || "N/A"}
                    </Typography>
                  </TableCell>
                )}

                {visibleColumns > 5 && (
                  <TableCell
                    align="center"
                    sx={{ padding: isMobile ? "4px" : "8px" }}
                  >
                    <Typography
                      sx={{
                        fontSize: isMobile ? "0.7rem" : "0.875rem",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        gap: "4px",
                      }}
                    >
                      <img
                        src="/BrandIcons/MDI - gas-station.svg"
                        alt="Fuel"
                        sx={{ fontSize: isMobile ? "0.9rem" : "1rem" }}
                      />
                      {car.carFuel || "N/A"}
                    </Typography>
                  </TableCell>
                )}
                {/* 
                {visibleColumns > 6 && (
                  <TableCell
                    align="center"
                    sx={{ padding: isMobile ? "4px" : "8px" }}
                  >
                    <Typography
                      sx={{ fontSize: isMobile ? "0.7rem" : "0.875rem" }}
                    >
                      {car.airConditioning || "N/A"}
                    </Typography>
                  </TableCell>
                )} */}

                {visibleColumns > 7 && (
                  <TableCell
                    align="center"
                    sx={{ padding: isMobile ? "4px" : "8px" }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <Box
                        sx={{
                          width: isMobile ? 12 : 16,
                          height: isMobile ? 12 : 16,
                          borderRadius: "50%",
                          backgroundColor:
                            car.carColor?.toLowerCase() || "#ccc",
                          border: "1px solid #ddd",
                          mr: 0.5,
                        }}
                      />
                      <Typography
                        sx={{ fontSize: isMobile ? "0.7rem" : "0.875rem" }}
                      >
                        {car.carColor || "N/A"}
                      </Typography>
                    </Box>
                  </TableCell>
                )}

                {visibleColumns > 8 && (
                  <TableCell
                    align="center"
                    sx={{ padding: isMobile ? "4px" : "8px" }}
                  >
                    <Typography
                      sx={{
                        fontSize: isMobile ? "0.7rem" : "0.875rem",
                        fontFamily: "monospace",
                      }}
                    >
                      {car.carChassisNumber || "N/A"}
                    </Typography>
                  </TableCell>
                )}

                {visibleColumns > 9 && (
                  <TableCell
                    align="center"
                    sx={{ padding: isMobile ? "4px" : "8px" }}
                  >
                    <Typography
                      sx={{
                        fontWeight: "bold",
                        fontSize: isMobile ? "0.75rem" : "0.95rem",
                        color: "error.main",
                      }}
                    >
                      {car.carPrice
                        ? `$${car.carPrice.toLocaleString()}`
                        : "On Request"}
                    </Typography>
                  </TableCell>
                )}

                <TableCell align="center">
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      justifyContent: "center",
                      gap: 0.8, // Slightly reduced gap between buttons
                      position: "relative",
                      padding: "6px 0", // Reduced padding
                    }}
                  >
                    <Button
                      variant="contained"
                      size="small" // Already using small size
                      sx={{
                        bgcolor: "transparent",
                        color: "red",
                        border: "1px solid red",
                        borderRadius: "16px", // Smaller border radius
                        fontSize: "0.75rem", // Smaller font size
                        padding: "3px 12px", // Reduced padding
                        minWidth: "80px", // Reduced minimum width
                        height: "28px", // Explicitly set smaller height
                        transition: "all 0.3s ease",
                        overflow: "hidden",
                        position: "relative",
                        zIndex: 1,
                        "&::before": {
                          content: '""',
                          position: "absolute",
                          top: 0,
                          left: 0,
                          width: "100%",
                          height: "100%",
                          background: "red",
                          transform: "translateX(-100%)",
                          transition: "transform 0.3s ease",
                          zIndex: -1,
                        },
                        "&:hover": {
                          color: "white",
                          boxShadow: "0 3px 6px rgba(255,0,0,0.3)", // Smaller shadow
                          "&::before": {
                            transform: "translateX(0)",
                          },
                        },
                      }}
                      onClick={(e) => {
                        e.stopPropagation(); // Add this line
                        setSelectedCarModelId(car.carModelId); // Store the clicked car's model ID
                        handleModalOpen();
                      }}
                    >
                      INQUIRY
                    </Button>

                    {/* <Button
                      variant="contained"
                      size="small" // Already using small size
                      sx={{
                        bgcolor: "red",
                        color: "white",
                        borderRadius: "16px", // Smaller border radius
                        fontSize: "0.75rem", // Smaller font size
                        padding: "3px 12px", // Reduced padding
                        minWidth: "80px", // Reduced minimum width
                        height: "28px", // Explicitly set smaller height
                        position: "relative",
                        overflow: "hidden",
                        transition: "all 0.3s ease",
                        "&:disabled": {
                          bgcolor: "#cccccc",
                          cursor: "not-allowed",
                        },
                        "&::after": {
                          content: '""',
                          position: "absolute",
                          width: "30px",
                          height: "100%",
                          top: 0,
                          left: "-50px",
                          backgroundColor: "rgba(255,255,255,0.5)",
                          transform: "skewX(-25deg)",
                          transition: "all 0.4s ease",
                        },
                        "&:hover": {
                          bgcolor: "#c00",
                          boxShadow: "0 4px 10px rgba(255,0,0,0.3)", // Smaller shadow
                          "&::after": {
                            left: "120%",
                          },
                        },
                      }}
                      disabled={cartItems.some(
                        (item) => item.modelId === car.carModelId
                      )}
                      onClick={(e) => {
                        e.stopPropagation();
                        if (isFavorite) {
                          setDialogType("move");
                          setSelectedModelId(car.carModelId);
                          setOpenDialog(true);
                        } else {
                          onAddToCart(car.carModelId);
                        }
                      }}
                    >
                      {isFavorite
                        ? "MOVE TO CART"
                        : cartItems.some(
                          (item) => item.modelId === car.carModelId
                        )
                          ? "ADDED"
                          : "ADD TO CART"}
                    </Button> */}
                  </Box>
                </TableCell>
              </StyledTableRow>
            );
          })}
        </TableBody>
      </Table>







      <Modal
        open={open}
        onClose={handleModalClose}
        aria-labelledby="inquiry-modal-title"
        aria-describedby="inquiry-modal-description"
      // sx={{ position: "absolute" }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            height: "100vh",
          }}
        // onClick={handleModalClose}
        >
          <Container
            maxWidth="md"
            sx={{
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              bgcolor: "white",
              borderRadius: "8px",
              boxShadow: 24,
              width: "90%",
              maxHeight: "90vh",
              overflow: "auto",
              p: 4,
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <Box sx={{
              display: 'flex',
              justifyContent: 'flex-end',
              position: 'absolute',
              top: 0,
              right: 8,
              zIndex: 1000
            }}>
              <IconButton
                onClick={handleModalClose}
              >
                <CloseIcon />
              </IconButton>
            </Box>
            {productLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '200px' }}>
                <CircularProgress />
              </Box>
            ) : product ? (
              <Box sx={{ maxHeight: "80vh", overflowY: "auto", p: 2 }}>
                <Typography variant="h4" fontWeight="bold" gutterBottom>
                  Get a Free Quotation
                </Typography>
                <Typography variant="subtitle1" gutterBottom>
                  Send us your enquiry, our sales team will contact you.
                </Typography>

                <Grid container spacing={4}>
                  <Grid item xs={12} md={6}>
                    <CardMedia
                      component="img"
                      height="300"
                      image={selectedCarModelId ?
                        `https://car.amar.co.jp/api/image/car-exterior-images-1/get-image/${selectedCarModelId}` :
                        "/carousel/carslider.png"
                      }
                      alt={`${product.carBrandName} ${product.carModelName}`}
                      onError={(e) => {
                        e.target.src = "/carousel/carslider.png";
                      }}
                      sx={{ borderRadius: '8px', objectFit: 'cover' }}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      <Rating value={5} readOnly size="large" />
                      <Typography variant="body2" sx={{ ml: 2 }}>15 User Comments</Typography>
                      <IconButton sx={{ ml: 'auto' }}><Share /></IconButton>
                    </Box>

                    <Typography variant="h4" fontWeight="bold" mt={2}>
                      {`${product.brandName} ${product.modelName} ${product.carCategoriesName || product.carCategoryName || product.categoryName || ''}`}
                    </Typography>
                    <Typography variant="subtitle1" color="text.secondary" sx={{ marginTop: '12px' }}>
                      {`${product.carMileage || 'N/A'} | ${product.carTransmission || 'N/A'} | ${product.carDrive || 'N/A'} | ${product.carEngineCC || 'N/A'}`}
                    </Typography>
                    <Typography variant="body1" sx={{ marginTop: '24px' }}>
                      {product.categoriesName} with {product.carColor} exterior
                    </Typography>

                    <Box sx={{ mt: 3 }}>
                      <Typography variant="h6" color="text.secondary" sx={{ textDecoration: "line-through" }}>
                        {`$${(product.carPrice * 1.2).toFixed(2)}`}
                      </Typography>
                      <Typography variant="h4" color="error" fontWeight="bold" sx={{ marginTop: '8px' }}>
                        {`$${product.carPrice.toFixed(2)}`}
                      </Typography>
                    </Box>
                  </Grid>
                </Grid>

                <Typography variant="h6" fontWeight="bold" sx={{ mt: 4, mb: 2 }}>
                  Car Specifications
                </Typography>
                <TableContainer component={Paper}>
                  <Table>
                    <TableBody>
                      <TableRow>
                        <TableCell sx={{ bgcolor: "#f5f5f5", fontWeight: "medium" }}>
                          Reference No.
                        </TableCell>
                        <TableCell>{product?.carRefNumber || ''}</TableCell>
                        <TableCell sx={{ bgcolor: "#f5f5f5", fontWeight: "medium" }}>
                          Drive Type
                        </TableCell>
                        <TableCell>{product?.carDrive || ''}</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell sx={{ bgcolor: "#f5f5f5", fontWeight: "medium" }}>
                          Registration Year
                        </TableCell>
                        <TableCell>{product?.year || ''}</TableCell>
                        <TableCell sx={{ bgcolor: "#f5f5f5", fontWeight: "medium" }}>
                          Model Code
                        </TableCell>
                        <TableCell>{product?.carModelCode || ''}</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell sx={{ bgcolor: "#f5f5f5", fontWeight: "medium" }}>
                          Manufacture Year
                        </TableCell>
                        <TableCell>{product?.carManufactureDate || ''}</TableCell>
                        <TableCell sx={{ bgcolor: "#f5f5f5", fontWeight: "medium" }}>
                          Model Grade
                        </TableCell>
                        <TableCell>{product?.carGrade || ''}</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell sx={{ bgcolor: "#f5f5f5", fontWeight: "medium" }}>
                          Mileage
                        </TableCell>
                        <TableCell>
                          {product?.carMileage || ''}
                          <br />
                          (Approx. 42,262 miles)
                        </TableCell>
                        <TableCell sx={{ bgcolor: "#f5f5f5", fontWeight: "medium" }}>
                          Transmission
                        </TableCell>
                        <TableCell>{product?.carTransmission || ''}</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell sx={{ bgcolor: "#f5f5f5", fontWeight: "medium" }}>
                          Fuel Type
                        </TableCell>
                        <TableCell>{product?.carFuel || ''}</TableCell>
                        <TableCell sx={{ bgcolor: "#f5f5f5", fontWeight: "medium" }}>
                          Engine CC / Motor kW
                        </TableCell>
                        <TableCell>
                          {product?.carEngineCC || ''}
                          <br />
                          (0.66 liters)
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell sx={{ bgcolor: "#f5f5f5", fontWeight: "medium" }}>
                          No. of Doors
                        </TableCell>
                        <TableCell>{product?.carDoor || ''}</TableCell>
                        <TableCell sx={{ bgcolor: "#f5f5f5", fontWeight: "medium" }}>
                          No. of Seats
                        </TableCell>
                        <TableCell>{product?.carSeat || ''}</TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </TableContainer>

                <Typography variant="h6" fontWeight="bold" sx={{ mt: 4, mb: 2 }}>
                  * Required fields
                </Typography>

                <Card sx={{
                  background: 'none',
                  border: '1px solid #ff3b30',
                  borderRadius: '16px',
                  padding: { xs: '16px', md: '32px' },
                  maxWidth: '800px',
                  margin: 'auto',
                  position: 'relative',
                  overflow: 'visible',
                  '&:before': {
                    content: '""',
                    position: 'absolute',
                    top: -2,
                    left: -2,
                    right: -2,
                    bottom: -2,
                    borderRadius: '16px',
                    zIndex: -1
                  }
                }}>
                  <CardContent>
                    <Typography variant="h5" sx={{
                      color: '#ff3b30',
                      mb: 4,
                      textAlign: 'center',
                      textTransform: 'uppercase',
                      fontWeight: 'bold',
                      letterSpacing: '1px'
                    }}>
                      Vehicle Inquiry Form
                    </Typography>

                    {/* Inquiry Type Selection */}
                    <Grid container spacing={3} sx={{ mb: 3 }}>
                      <Grid item xs={12}>
                        <FormControl fullWidth>
                          <RadioGroup
                            row
                            value={inquiryType}
                            onChange={(e) => setInquiryType(e.target.value)}
                            sx={{
                              display: 'flex',
                              gap: 2,
                              justifyContent: 'center',
                              '@media (max-width: 600px)': {
                                flexDirection: 'column'
                              }
                            }}
                          >
                            <FormControlLabel
                              value="general"
                              control={
                                <Radio
                                  sx={{
                                    color: '#ff3b30',
                                    '&.Mui-checked': {
                                      color: '#ff3b30',
                                    },
                                  }}
                                />
                              }
                              label="General Inquiry"
                              sx={{
                                m: 0,
                                border: '1px solid #ff3b30',
                                borderRadius: '8px',
                                padding: '8px 16px',
                                '&.Mui-focused': {
                                  borderColor: '#ff3b30',
                                },
                              }}
                            />
                            <FormControlLabel
                              value="shipping"
                              control={
                                <Radio
                                  sx={{
                                    color: '#ff3b30',
                                    '&.Mui-checked': {
                                      color: '#ff3b30',
                                    },
                                  }}
                                />
                              }
                              label="Request Shipping Quote"
                              sx={{
                                m: 0,
                                border: '1px solid #ff3b30',
                                borderRadius: '8px',
                                padding: '8px 16px',
                                '&.Mui-focused': {
                                  borderColor: '#ff3b30',
                                },
                              }}
                            />
                          </RadioGroup>
                        </FormControl>
                      </Grid>
                    </Grid>
                    {/* General Inquiry Form */}
                    {inquiryType === 'general' && (
                      <Grid container spacing={3}>
                        {/* Add refresh button at the top */}

                        {/* Personal Info Grid */}
                        <Grid item xs={12} container spacing={2}>
                          {['firstName', 'lastName', 'phoneNumber', 'emailId'].map((field) => (
                            <Grid item xs={12} md={6} key={field}>
                              <TextField
                                fullWidth
                                label={labelMap[field]}
                                value={formData[field]}
                                onChange={handleInputChangeGlobal(field)}
                                required
                                disabled={isLoadingUserDetails}
                                InputLabelProps={{
                                  shrink: true
                                }}
                                sx={{
                                  '& .MuiOutlinedInput-root': {
                                    borderRadius: '8px',
                                    color: '#333',
                                    '& fieldset': { borderColor: '#333' },
                                    '&:hover fieldset': { borderColor: '#ff3b30' },
                                    '&.Mui-focused fieldset': { borderColor: '#ff3b30' }
                                  },
                                  '& .MuiInputLabel-root': {
                                    color: '#888',
                                    backgroundColor: '#fff',
                                    paddingX: '4px',
                                    '&.Mui-focused': {
                                      color: '#ff3b30'
                                    }
                                  }
                                }}
                                InputProps={{
                                  style: {
                                    backgroundColor: '#fff'
                                  }
                                }}
                              />
                            </Grid>
                          ))}
                        </Grid>

                        {/* Message Field */}
                        <Grid item xs={12}>
                          <TextField
                            fullWidth
                            multiline
                            minRows={3}
                            label="Your Message"
                            value={formData.userMessage}
                            onChange={handleInputChange('userMessage')}
                            disabled={isLoadingUserDetails}
                            InputLabelProps={{
                              shrink: true
                            }}
                            sx={{
                              '& .MuiOutlinedInput-root': {
                                borderRadius: '8px',
                                color: '#333',
                                paddingY: '12px',
                                '& fieldset': { borderColor: '#333' },
                                '&:hover fieldset': { borderColor: '#ff3b30' },
                                '&.Mui-focused fieldset': { borderColor: '#ff3b30' }
                              },
                              '& .MuiInputLabel-root': {
                                color: '#888',
                                backgroundColor: '#fff',
                                paddingX: '4px',
                                '&.Mui-focused': {
                                  color: '#ff3b30'
                                }
                              }
                            }}
                            InputProps={{
                              style: {
                                backgroundColor: '#fff'
                              }
                            }}
                          />
                        </Grid>
                        {/* Submit Button */}
                        <Grid item xs={12} sx={{ mt: 2 }}>
                          <Button
                            fullWidth
                            variant="contained"
                            onClick={handleSubmitInquiry}
                            disabled={
                              !formData.firstName ||
                              !formData.lastName ||
                              !formData.phoneNumber ||
                              !formData.emailId ||
                              isSubmitting ||
                              isLoadingUserDetails
                            }
                            sx={{
                              background: '#ff3b30',
                              color: '#fff',
                              py: 2,
                              borderRadius: '8px',
                              fontWeight: 'bold',
                              fontSize: '1rem',
                              textTransform: 'uppercase',
                              letterSpacing: '1px',
                              transition: 'all 0.3s',
                              '&:hover': {
                                background: '#ff5047',
                                transform: 'translateY(-2px)',
                                boxShadow: '0 8px 24px rgba(255, 59, 48, 0.4)'
                              }
                            }}
                          >
                            {isSubmitting ? (
                              <>
                                <CircularProgress size={24} color="inherit" sx={{ mr: 2 }} />
                                Submitting Inquiry...
                              </>
                            ) : (
                              'Submit Inquiry'
                            )}
                          </Button>
                        </Grid>
                      </Grid>
                    )}

                    {/* Shipping Quote Form */}
                    {inquiryType === 'shipping' && (
                      <Grid container spacing={3}>
                        {/* Country Dropdown */}
                        <Grid item xs={12} md={6}>
                          <Autocomplete
                            options={countriesList}
                            getOptionLabel={(option) => option.countryName}
                            value={formData.country}
                            onChange={(event, newValue) =>
                              handleChange({ target: { name: 'country', value: newValue } })
                            }
                            onOpen={() => {
                              if (countriesList.length === 0) fetchCountries();
                            }}
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                label="Country"
                                variant="outlined"

                                InputLabelProps={{
                                  sx: { color: '#888', fontWeight: 500, shrink: true }
                                }}
                                sx={{
                                  '& .MuiOutlinedInput-root': {
                                    borderRadius: '8px',
                                    color: 'black !important',
                                    '& fieldset': {
                                      borderColor: '#ccc !important',
                                      borderWidth: '1px !important',
                                      borderStyle: 'solid !important'
                                    },
                                    '&:hover fieldset': {
                                      borderColor: '#ff3b30 !important',
                                      borderWidth: '1px !important',
                                      borderStyle: 'solid !important'
                                    },
                                    '&.Mui-focused fieldset': {
                                      borderColor: '#ff3b30 !important',
                                      borderWidth: '1px !important',
                                      borderStyle: 'solid !important'
                                    }
                                  }
                                }}
                              />
                            )}
                          />
                        </Grid>
                        {/* Add rest of shipping form fields here */}
                        {/* Port Dropdown */}
                        <Grid item xs={12} md={6}>
                          <Autocomplete
                            options={portsList}
                            getOptionLabel={(option) => option.portName}
                            value={formData.port}
                            onChange={handlePortChange}
                            disabled={!formData.country}
                            onOpen={() => {
                              if (formData.country) {
                                fetchPorts(formData.country.countryId);
                              }
                            }}
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                label="Port"
                                variant="outlined"
                                InputLabelProps={{ sx: { color: '#888', fontWeight: 500, shrink: true } }}
                                sx={{
                                  '& .MuiOutlinedInput-root': {
                                    borderRadius: '8px',
                                    color: 'black !important',
                                    '& fieldset': {
                                      borderColor: '#333 !important',
                                      borderWidth: '1px !important',
                                      borderStyle: 'solid !important'
                                    },
                                    '&:hover fieldset': {
                                      borderColor: '#ff3b30 !important',
                                      borderWidth: '1px !important',
                                      borderStyle: 'solid !important'
                                    },
                                    '&.Mui-focused fieldset': {
                                      borderColor: '#ff3b30 !important',
                                      borderWidth: '1px !important',
                                      borderStyle: 'solid !important'
                                    }
                                  }
                                }}
                              />
                            )}
                          />
                        </Grid>

                        {/* Shipping Options - Compact Toggle Buttons */}
                        <Grid item xs={12}>
                          <FormControl fullWidth>
                            <Grid item xs={12}>
                              <Typography variant="body2" sx={{
                                color: '#ff3b30',
                                fontWeight: '700',
                                fontSize: '0.95rem',
                                mb: 2.5,
                                position: 'relative',
                                '&:after': {
                                  content: '""',
                                  position: 'absolute',
                                  bottom: -4,
                                  left: 0,
                                  width: '40px',
                                  height: '2px',
                                  backgroundColor: '#ff3b30'
                                }
                              }}>
                                Shipping Options
                              </Typography>
                            </Grid>
                            <RadioGroup
                              value={formData.shippingOption}
                              onChange={(e) => handleChange({ target: { name: 'shippingOption', value: e.target.value } })}
                              sx={{
                                display: 'flex',
                                gap: 2,
                                flexDirection: 'row',
                                '@media (max-width: 600px)': {
                                  flexDirection: 'column'
                                }
                              }}
                              disabled={!formData.port}
                            >
                              {shippingOptions.map((option) => (
                                <FormControlLabel
                                  key={option.value}
                                  value={option.value}
                                  control={
                                    <Radio
                                      sx={{
                                        color: '#ff3b30',
                                        '&.Mui-checked': {
                                          color: '#ff3b30',
                                        },
                                      }}
                                    />
                                  }
                                  label={
                                    <Typography variant="body2" fontWeight="500" color="#333">
                                      {option.label.split(' (')[0]}
                                    </Typography>
                                  }
                                  sx={{
                                    flex: 1,
                                    m: 0,
                                    border: '1px solid #ff3b30',
                                    borderRadius: '8px',
                                    padding: '8px 16px',
                                    '&.Mui-focused': {
                                      borderColor: '#ff3b30',
                                    },
                                    '&.Mui-selected': {
                                      backgroundColor: 'rgba(255, 59, 48, 0.1)',
                                    }
                                  }}
                                />
                              ))}
                            </RadioGroup>
                          </FormControl>
                        </Grid>

                        {/* Fee Configuration List */}
                        <Grid item xs={12}>
                          <Typography variant="body2" sx={{
                            color: '#ff3b30',
                            fontWeight: '700',
                            fontSize: '0.95rem',
                            mb: 0.5,
                            position: 'relative',
                            '&:after': {
                              content: '""',
                              position: 'absolute',
                              bottom: -4,
                              left: 0,
                              width: '40px',
                              height: '2px',
                              backgroundColor: '#ff3b30'
                            }
                          }}>
                            Fee Configuration List
                          </Typography>
                        </Grid>

                        {/* Fee Configuration Section */}
                        <Grid item xs={12} sx={{ mb: 3 }}>
                          <Box sx={{
                            backgroundColor: '#f8f9fa',
                            borderRadius: '12px',
                            p: 2.5,
                            boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
                            border: '1px solid #e0e0e0'
                          }}>
                            <Typography variant="subtitle1" sx={{
                              color: '#333',
                              fontWeight: '600',
                              mb: 2,
                              display: 'flex',
                              alignItems: 'center',
                              gap: 1
                            }}>
                              <ReceiptIcon fontSize="small" />
                              FEE BREAKDOWN
                            </Typography>
                            {/* Fee Configuration Grid */}
                            <Grid container spacing={1.5}>

                              {feeConfigurations.map(({ key, label, apiKey, isBaseCost }) => (
                                <Grid item xs={12} sm={6} md={4} key={key}>
                                  <Box sx={{

                                    display: 'flex',

                                    justifyContent: 'space-between',

                                    alignItems: 'center',

                                    backgroundColor: isBaseCost ? 'rgba(33, 150, 243, 0.08)' : 'white',

                                    borderRadius: '8px',

                                    p: '10px 15px',

                                    boxShadow: '0 1px 3px rgba(0,0,0,0.05)',

                                    borderLeft: `3px solid ${isBaseCost ? '#2196f3' : '#ff3b30'}`,

                                    height: '100%',

                                    transition: 'all 0.2s ease',

                                    '&:hover': {

                                      transform: 'translateY(-2px)',

                                      boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1)'

                                    }

                                  }}>
                                    <Box>
                                      <Typography variant="caption" sx={{

                                        color: isBaseCost ? '#1976d2' : '#666',

                                        fontWeight: '500',

                                        fontSize: '0.7rem',

                                        display: 'block'

                                      }}>

                                        {label}
                                      </Typography>
                                      <Typography variant="body2" sx={{

                                        color: isBaseCost ? '#1976d2' : '#333',

                                        fontWeight: '600',

                                        fontSize: '0.9rem',

                                        letterSpacing: '-0.3px'

                                      }}>

                                        ${shippingCostData?.charges[apiKey].toFixed(2) || '0.00'}
                                      </Typography>
                                    </Box>
                                    <InfoOutlinedIcon sx={{

                                      color: isBaseCost ? '#64b5f6' : '#999',

                                      fontSize: '1rem',

                                      '&:hover': {

                                        color: isBaseCost ? '#1976d2' : '#ff3b30',

                                        cursor: 'pointer'

                                      }

                                    }} />
                                  </Box>
                                </Grid>

                              ))}
                            </Grid>

                            {/* Subtotal of Fees */}
                            <Box sx={{

                              mt: 2,

                              p: 2,

                              backgroundColor: 'rgba(255, 59, 48, 0.05)',

                              borderRadius: '8px',

                              border: '1px dashed rgba(255, 59, 48, 0.3)',

                              display: 'flex',

                              justifyContent: 'space-between',

                              alignItems: 'center'

                            }}>
                              <Typography variant="body1" sx={{

                                fontWeight: '600',

                                color: '#333'

                              }}>

                                Subtotal Fees
                              </Typography>
                              <Typography variant="body1" sx={{

                                fontWeight: '700',

                                color: '#ff3b30'

                              }}>

                                ${feeConfigurations

                                  .filter(fee => !fee.isBaseCost)  // Exclude base cost

                                  .reduce((total, { apiKey }) =>

                                    total + (shippingCostData?.charges[apiKey] || 0), 0).toFixed(2)}
                              </Typography>
                            </Box>


                          </Box>
                        </Grid>

                        {/* Total Cost Section */}
                        <Grid item xs={12}>
                          <Box sx={{
                            border: '1px solid #e0e0e0',
                            borderRadius: '12px',
                            p: 3,
                            backgroundColor: 'white',
                            boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
                            position: 'relative',
                            overflow: 'hidden',
                            '&:before': {
                              content: '""',
                              position: 'absolute',
                              top: 0,
                              left: 0,
                              width: '5px',
                              height: '100%',
                              backgroundColor: '#ff3b30'
                            }
                          }}>
                            <Typography variant="h6" sx={{
                              color: '#333',
                              fontWeight: '700',
                              fontSize: '1.1rem',
                              mb: 3,
                              display: 'flex',
                              alignItems: 'center',
                              gap: 1.5
                            }}>
                              <CalculateIcon sx={{ color: '#ff3b30', fontSize: '1.4rem' }} />
                              TOTAL COST SUMMARY
                            </Typography>

                            {/* Cost Breakdown */}
                            <Grid container spacing={2} sx={{ mb: 2 }}>
                              {[

                                {

                                  label: 'Vehicle Price',

                                  value: product?.carPrice.toFixed(2) || '0.00',

                                  icon: <DirectionsCarIcon sx={{ color: '#ff3b30' }} />,

                                  tooltip: 'Base price of the vehicle before any fees'

                                },

                                {

                                  label: 'm³ Charge',

                                  value: (shippingCostData?.charges.baseShippingCost || 0).toFixed(2),

                                  icon: <StraightenIcon sx={{ color: '#2196f3' }} />,

                                  tooltip: 'Base shipping cost based on vehicle volume',

                                  color: '#1976d2'

                                },

                                {

                                  label: 'Port Charges',

                                  value: feeConfigurations

                                    .filter(fee => !fee.isBaseCost)

                                    .reduce((total, { apiKey }) =>

                                      total + (shippingCostData?.charges[apiKey] || 0), 0).toFixed(2),

                                  icon: <LocalShippingIcon sx={{ color: '#ff3b30' }} />,

                                  tooltip: 'Total of all port charges and additional fees'

                                },

                                {

                                  label: 'Estimated Total',

                                  value: (shippingCostData?.charges.totalCost ?? product?.carPrice)?.toFixed(2) || '0.00',

                                  icon: <AttachMoneyIcon sx={{ color: '#ff3b30' }} />,

                                  tooltip: 'Final estimated amount including all costs'

                                }

                              ].map((field) => (
                                <Grid item xs={12} md={3} key={field.label}>
                                  <Tooltip title={field.tooltip} arrow>
                                    <Box sx={{

                                      backgroundColor: 'rgba(255,255,255,0.95)',

                                      borderRadius: '8px',

                                      p: 2,

                                      border: '1px solid rgba(0, 0, 0, 0.08)',

                                      height: '100%',

                                      transition: 'all 0.3s ease',

                                      '&:hover': {

                                        transform: 'translateY(-3px)',

                                        boxShadow: '0 8px 16px rgba(0, 0, 0, 0.1)'

                                      }

                                    }}>
                                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>

                                        {field.icon}
                                        <Typography variant="body2" sx={{

                                          color: field.color || '#666',

                                          fontWeight: '600',

                                          fontSize: '0.9rem'

                                        }}>

                                          {field.label}
                                        </Typography>
                                      </Box>
                                      <Typography variant="h6" sx={{

                                        color: field.label === 'Estimated Total' ? '#ff3b30' : (field.color || '#333'),

                                        fontWeight: field.label === 'Estimated Total' ? '800' : '700',

                                        fontSize: '1.3rem',

                                        pl: 3.5

                                      }}>

                                        ${field.value}
                                      </Typography>
                                    </Box>
                                  </Tooltip>
                                </Grid>

                              ))}


                            </Grid>

                            {/* Payment Note */}
                            <Box sx={{
                              mt: 2,
                              p: 2,
                              backgroundColor: 'rgba(255, 235, 59, 0.15)',
                              borderRadius: '8px',
                              borderLeft: '3px solid #ffc107'
                            }}>
                              <Typography variant="body2" sx={{
                                color: '#666',
                                fontWeight: '500',
                                display: 'flex',
                                alignItems: 'flex-start',
                                gap: 1
                              }}>
                                <InfoOutlinedIcon fontSize="small" sx={{ color: '#ffc107', mt: '2px' }} />
                                Final amount may vary based on currency exchange rates at time of payment. A 10% deposit is required to secure your vehicle.
                              </Typography>
                            </Box>
                          </Box>
                        </Grid>

                        {/* Personal Info Grid */}
                        <Grid item xs={12} container spacing={2}>
                          {['firstName', 'lastName', 'phoneNumber', 'emailId'].map((field) => (
                            <Grid item xs={12} md={6} key={field}>
                              <TextField
                                fullWidth
                                label={labelMap[field]}
                                value={formData[field]}
                                onChange={handleInputChange(field)}
                                required
                                InputLabelProps={{
                                  shrink: true
                                }}
                                sx={{
                                  '& .MuiOutlinedInput-root': {
                                    borderRadius: '8px',
                                    color: '#333',
                                    '& fieldset': { borderColor: '#333' },
                                    '&:hover fieldset': { borderColor: '#ff3b30' },
                                    '&.Mui-focused fieldset': { borderColor: '#ff3b30' }
                                  },
                                  '& .MuiInputLabel-root': {
                                    color: '#888',
                                    '&.Mui-focused': {
                                      color: '#ff3b30'
                                    }
                                  }
                                }}
                                InputProps={{
                                  style: {
                                    backgroundColor: '#fff'
                                  }
                                }}
                              />
                            </Grid>
                          ))}
                        </Grid>

                        {/* Message Field */}
                        <Grid item xs={12}>
                          <TextField
                            fullWidth
                            multiline
                            minRows={3}
                            label="Your Message"
                            value={formData.userMessage}
                            onChange={handleInputChange('userMessage')}
                            InputLabelProps={{
                              shrink: true
                            }}
                            sx={{
                              '& .MuiOutlinedInput-root': {
                                borderRadius: '8px',
                                color: '#333',
                                '& fieldset': { borderColor: '#333' },
                                '&:hover fieldset': { borderColor: '#ff3b30' }
                              },
                              '& .MuiInputLabel-root': { color: '#888' }
                            }}
                          />
                        </Grid>

                        {/* Submit Button */}
                        <Grid item xs={12} sx={{ mt: 2 }}>
                          <Button
                            fullWidth
                            variant="contained"
                            onClick={handleSubmitInquiry}
                            disabled={!isPortSelected || isSubmitting}
                            sx={{
                              background: '#ff3b30',
                              color: '#fff',
                              py: 2,
                              borderRadius: '8px',
                              fontWeight: 'bold',
                              fontSize: '1rem',
                              textTransform: 'uppercase',
                              letterSpacing: '1px',
                              transition: 'all 0.3s',
                              '&:hover': {
                                background: '#ff5047',
                                transform: 'translateY(-2px)',
                                boxShadow: '0 8px 24px rgba(255, 59, 48, 0.4)'
                              }
                            }}
                          >
                            {isSubmitting ? (
                              <>
                                <CircularProgress size={24} color="inherit" sx={{ mr: 2 }} />
                                Submitting Inquiry...
                              </>
                            ) : (
                              'Submit Inquiry'
                            )}
                          </Button>
                        </Grid>
                      </Grid>
                    )}

                    <Snackbar
                      open={showSuccessPopup}
                      autoHideDuration={2000}
                      onClose={() => {
                        setShowSuccessPopup(false);
                        handleModalClose();
                      }}
                      anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
                    >
                      <Alert
                        onClose={() => {
                          setShowSuccessPopup(false);
                          handleModalClose();
                        }}
                        severity="success"
                        sx={{ width: '100%' }}
                      >
                        Inquiry sent successfully!
                      </Alert>
                    </Snackbar>

                    {/* Error Snackbar */}
                    <Snackbar
                      open={showErrorPopup}
                      autoHideDuration={6000}
                      onClose={() => setShowErrorPopup(false)}
                      anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
                    >
                      <Alert
                        onClose={() => setShowErrorPopup(false)}
                        severity="error"
                        sx={{ width: '100%' }}
                      >
                        Failed to submit inquiry. Please try again.
                      </Alert>
                    </Snackbar>
                  </CardContent>
                </Card>
              </Box>
            ) : (
              <Typography>Product not found</Typography>
            )}
          </Container>
        </Box>
      </Modal>
      {/* Confirmation Dialog */}
      <Dialog
        open={openDialog}
        onClose={() => {
          setOpenDialog(false);
          setSelectedModelId(null);
          setDialogType("");
        }}
      >
        <DialogTitle>
          {dialogType === "move"
            ? "Confirm Move to Cart"
            : "Remove from Wishlist"}
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            {dialogType === "move"
              ? "Are you sure you want to move this item to your cart?"
              : "Remove this item from wishlist?"}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <MuiButton
            onClick={() => {
              setOpenDialog(false);
              setSelectedModelId(null);
              setDialogType("");
            }}
          >
            Cancel
          </MuiButton>
          <MuiButton
            onClick={handleConfirmAction}
            color="primary"
            variant="contained"
          >
            Confirm
          </MuiButton>
        </DialogActions>
      </Dialog>
    </StyledTableContainer>
  );
};

const MoreFilterByGlobalSearch = () => {
  // --- State Hooks ---
  const [imageCache, setImageCache] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [showFilter, setShowFilter] = useState(false);
  const [viewMode, setViewMode] = useState("list");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(20);
  const [totalPages, setTotalPages] = useState(1);
  const [hasInitialized, setHasInitialized] = useState(false);
  const [globalSearchTerm, setGlobalSearchTerm] = useState('');

  // Single source of truth for filter state
  const [filterState, setFilterState] = useState({
    selectedCategory: null,
    selectedBrand: null,
    selectedModels: [],
    selectedYears: [],
    colorSelect: '',
    carDriveType: '',
    carEngine: '',
    carFilterAccessories: [],
    carFuelType: '',
    carMileage: '',
    carPrice: '',
    carSeat: '',
    carSteering: '',
    carTransmission: '',
    globalSearch: ''
  });

  // Refs to prevent unnecessary effects
  const isFetchingRef = useRef(false);
  const lastFetchParamsRef = useRef(null);
  const initializationRef = useRef(false);
  const showAllCarsRequestedRef = useRef(false);

  // --- External Hooks and Redux Selectors ---
  const { enqueueSnackbar } = useSnackbar();
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const cartItems = useSelector((state) => state.cart?.items || []);
  const reduxSearchParams = useSelector(state => state.searchParams);
  const searchResults = useSelector(state => state.searchResults);
  const totalItems = useSelector(state => state.totalItems);
  const loading = useSelector(state => state.loading);

  const finalResults = Array.isArray(searchResults)
    ? searchResults
    : searchResults?.data || [];

  // Environment-based API configuration
  const API_BASE_URL = process.env.NODE_ENV === 'production'
    ? process.env.REACT_APP_API_BASE_URL || 'https://car.amar.co.jp'
    : 'https://car.amar.co.jp';

  // --- Utility Callbacks ---

  const getCacheBustedUrl = useCallback((url) => {
    if (process.env.NODE_ENV === 'development') {
      return url;
    }
    const separator = url.includes('?') ? '&' : '?';
    const timestamp = new Date().getTime();
    return `${url}${separator}cacheBuster=${timestamp}`;
  }, []);

  const fetchCarImage = useCallback(async (carModelId) => {
    try {
      const cacheBustedUrl = getCacheBustedUrl(
        `${API_BASE_URL}/api/image/car-exterior-images-1/get-image/${carModelId}`
      );

      const response = await fetch(cacheBustedUrl, {
        headers: {
          'Cache-Control': 'no-cache, no-store, must-revalidate',
          'Pragma': 'no-cache',
          'Expires': '0'
        },
        credentials: 'include'
      });

      if (response.ok) {
        const blob = await response.blob();
        return URL.createObjectURL(blob);
      }
      return "/carousel/carslider.png";
    } catch (error) {
      console.error("Error fetching car image:", error);
      return "/carousel/carslider.png";
    }
  }, [API_BASE_URL, getCacheBustedUrl]);

  // Enhanced URL parameter parsing that matches FilterByGlobalSearch
  const parseURLParams = useCallback(() => {
    const searchParams = new URLSearchParams(location.search);
    const params = {
      selectedCategory: null,
      selectedBrand: null,
      selectedModels: [],
      selectedYears: [],
      colorSelect: '',
      carDriveType: '',
      carEngine: '',
      carFilterAccessories: [],
      carFuelType: '',
      carMileage: '',
      carPrice: '',
      carSeat: '',
      carSteering: '',
      carTransmission: '',
      globalSearch: ''
    };

    // Handle both naming conventions from both components
    const vehicleType = searchParams.get('categoryName') || searchParams.get('vehicleType') || '';
    const brand = searchParams.get('brandName') || searchParams.get('brand') || '';

    let models = searchParams.getAll('model').filter(m => m && m.trim() !== '');
    if (models.length === 0) {
      const modelString = searchParams.get('modelNames');
      if (modelString) {
        models = modelString.split(',').filter(m => m && m.trim() !== '');
      }
    }

    let years = searchParams.getAll('year').filter(y => y).map(y => parseInt(y)).filter(y => !isNaN(y));
    if (years.length === 0) {
      const yearString = searchParams.get('years');
      if (yearString) {
        years = yearString.split(',').map(y => parseInt(y)).filter(y => !isNaN(y));
      }
    }

    const globalSearch = searchParams.get('search') || '';

    // Get all filter parameters
    params.colorSelect = searchParams.get('colorSelect') || '';
    params.carDriveType = searchParams.get('carDriveType') || '';
    params.carEngine = searchParams.get('carEngine') || '';
    params.carFuelType = searchParams.get('carFuelType') || '';
    params.carMileage = searchParams.get('carMileage') || '';
    params.carPrice = searchParams.get('carPrice') || '';
    params.carSeat = searchParams.get('carSeat') || '';
    params.carSteering = searchParams.get('carSteering') || '';
    params.carTransmission = searchParams.get('carTransmission') || '';
    params.carFilterAccessories = searchParams.getAll('carFilterAccessories').filter(a => a && a.trim() !== '');
    params.globalSearch = globalSearch;

    // Convert to consistent structure
    params.selectedCategory = vehicleType ? { label: vehicleType } : null;
    params.selectedBrand = brand ? { label: brand } : null;
    params.selectedModels = models.map(m => ({ carModelName: m }));
    params.selectedYears = years.map(y => ({ year: y }));

    return params;
  }, [location.search]);

  // Enhanced URL update function that maintains consistency
  const updateURLWithFilters = useCallback((searchParams, page, size) => {
    const queryParams = new URLSearchParams();

    // Use consistent parameter names that work with both components
    if (searchParams.selectedCategory?.label) {
      queryParams.append('categoryName', searchParams.selectedCategory.label);
      queryParams.append('vehicleType', searchParams.selectedCategory.label); // Add both for compatibility
    }
    if (searchParams.selectedBrand?.label) {
      queryParams.append('brandName', searchParams.selectedBrand.label);
      queryParams.append('brand', searchParams.selectedBrand.label); // Add both for compatibility
    }

    // Models
    if (searchParams.selectedModels && Array.isArray(searchParams.selectedModels)) {
      searchParams.selectedModels.forEach(model => {
        if (model?.carModelName) {
          queryParams.append('model', model.carModelName);
        }
      });
      // Also add as modelNames for compatibility
      const modelNames = searchParams.selectedModels.map(m => m.carModelName).join(',');
      if (modelNames) {
        queryParams.append('modelNames', modelNames);
      }
    }

    // Years
    if (searchParams.selectedYears && Array.isArray(searchParams.selectedYears)) {
      searchParams.selectedYears.forEach(year => {
        if (year?.year) {
          queryParams.append('year', year.year.toString());
        }
      });
      // Also add as years for compatibility
      const years = searchParams.selectedYears.map(y => y.year).join(',');
      if (years) {
        queryParams.append('years', years);
      }
    }

    // Advanced filters
    if (searchParams.colorSelect) queryParams.append('colorSelect', searchParams.colorSelect);
    if (searchParams.carDriveType) queryParams.append('carDriveType', searchParams.carDriveType);
    if (searchParams.carEngine) queryParams.append('carEngine', searchParams.carEngine);
    if (searchParams.carFuelType) queryParams.append('carFuelType', searchParams.carFuelType);
    if (searchParams.carMileage) queryParams.append('carMileage', searchParams.carMileage);
    if (searchParams.carPrice) queryParams.append('carPrice', searchParams.carPrice);
    if (searchParams.carSeat) queryParams.append('carSeat', searchParams.carSeat);
    if (searchParams.carSteering) queryParams.append('carSteering', searchParams.carSteering);
    if (searchParams.carTransmission) queryParams.append('carTransmission', searchParams.carTransmission);

    // Accessories
    if (searchParams.carFilterAccessories && Array.isArray(searchParams.carFilterAccessories)) {
      searchParams.carFilterAccessories.forEach(accessory => {
        if (accessory) {
          queryParams.append('carFilterAccessories', accessory);
        }
      });
    }

    // Global search
    if (searchParams.globalSearch) queryParams.append('search', searchParams.globalSearch);

    // Pagination
    queryParams.append('page', page.toString());
    queryParams.append('size', size.toString());

    const newParamsString = queryParams.toString();

    // Update URL without triggering navigation
    navigate({ search: `?${newParamsString}` }, {
      replace: true,
      state: { filterState: searchParams } // Store state in location for consistency
    });
  }, [navigate]);

  // Enhanced fetch function with proper state synchronization - FIXED FOR SHOW ALL CARS
  const fetchResults = useCallback(async (params, pageIndex = null, pageSize = null, isInitialLoad = false, isShowAllCars = false) => {
    // Prevent duplicate fetches
    if (isFetchingRef.current) {
      console.log('Fetch already in progress, skipping...');
      return;
    }

    const page = pageIndex !== null ? pageIndex : Math.max(0, currentPage - 1);
    const size = pageSize !== null ? pageSize : itemsPerPage;

    const searchParams = {
      selectedCategory: params?.selectedCategory || null,
      selectedBrand: params?.selectedBrand || null,
      selectedModels: params?.selectedModels || [],
      selectedYears: params?.selectedYears || [],
      colorSelect: params?.colorSelect || '',
      carDriveType: params?.carDriveType || '',
      carEngine: params?.carEngine || '',
      carFilterAccessories: params?.carFilterAccessories || [],
      carFuelType: params?.carFuelType || '',
      carMileage: params?.carMileage || '',
      carPrice: params?.carPrice || '',
      carSeat: params?.carSeat || '',
      carSteering: params?.carSteering || '',
      carTransmission: params?.carTransmission || '',
      globalSearch: params?.globalSearch || ''
    };

    // Create a unique key for this fetch
    const fetchKey = JSON.stringify({ ...searchParams, page, size, isShowAllCars });

    // Check if we just fetched with the same params
    if (lastFetchParamsRef.current === fetchKey && !isInitialLoad) {
      console.log('Same params as last fetch, skipping...');
      return;
    }

    // FIXED: Allow fetch when there are no search criteria for "Show All Cars" scenario
    const hasSearchCriteria =
      searchParams.selectedCategory || searchParams.selectedBrand || searchParams.selectedModels.length > 0 ||
      searchParams.selectedYears.length > 0 || searchParams.colorSelect || searchParams.carDriveType ||
      searchParams.carEngine || searchParams.carFilterAccessories.length > 0 || searchParams.carFuelType ||
      searchParams.carMileage || searchParams.carPrice || searchParams.carSeat || searchParams.carSteering ||
      searchParams.carTransmission || (searchParams.globalSearch && searchParams.globalSearch.trim() !== '');

    // Allow fetch if: has search criteria OR is initial load OR is show all cars request
    const shouldFetch = hasSearchCriteria || isInitialLoad || isShowAllCars || showAllCarsRequestedRef.current;

    if (!shouldFetch && !isInitialLoad) {
      console.log('No search criteria and not initial load, skipping fetch');
      setIsLoading(false);
      return;
    }

    isFetchingRef.current = true;
    lastFetchParamsRef.current = fetchKey;
    setIsLoading(true);

    try {
      console.log('Fetching results:', { 
        page, 
        size, 
        hasSearchCriteria,
        isInitialLoad,
        isShowAllCars,
        showAllCarsRequested: showAllCarsRequestedRef.current
      });

      const response = await dispatch(performAdvanceFilter(
        searchParams.selectedCategory,
        searchParams.selectedBrand,
        searchParams.selectedModels,
        searchParams.selectedYears,
        page,
        size,
        searchParams.colorSelect,
        searchParams.carDriveType,
        searchParams.carEngine,
        searchParams.carFilterAccessories,
        searchParams.carFuelType,
        searchParams.carMileage,
        searchParams.carPrice,
        searchParams.carSeat,
        searchParams.carSteering,
        searchParams.carTransmission
      ));

      if (response && response.data) {
        // Update pagination first
        const newPage = page + 1;
        const newSize = size;

        if (newPage !== currentPage) setCurrentPage(newPage);
        if (newSize !== itemsPerPage) setItemsPerPage(newSize);

        // Update total pages
        if (response.totalPages) {
          setTotalPages(response.totalPages);
        } else {
          const totalFilteredItems = totalItems || (response.data.length > 0 ? response.data.length : 0);
          setTotalPages(Math.ceil(totalFilteredItems / size) || 1);
        }

        // CRITICAL: Update filter state to maintain selections
        setFilterState(prevState => ({
          ...prevState,
          ...searchParams
        }));

        // Update global search term if it changed
        if (searchParams.globalSearch !== globalSearchTerm) {
          setGlobalSearchTerm(searchParams.globalSearch || '');
        }

        // Update URL last to maintain state consistency
        updateURLWithFilters(searchParams, newPage, newSize);

        // Reset show all cars flag after successful fetch
        if (isShowAllCars || showAllCarsRequestedRef.current) {
          showAllCarsRequestedRef.current = false;
        }

      } else {
        setTotalPages(1);
      }

    } catch (error) {
      console.error("Error fetching results:", error);
      enqueueSnackbar('Error fetching results. Please try again.', { variant: 'error' });
      setTotalPages(1);
    } finally {
      setIsLoading(false);
      isFetchingRef.current = false;
    }
  }, [dispatch, enqueueSnackbar, updateURLWithFilters, totalItems, currentPage, itemsPerPage, globalSearchTerm]);

  // --- Effects ---

  // 1. Initial Load - ONLY ON MOUNT
  useEffect(() => {
    const initializeData = async () => {
      if (initializationRef.current) return;
      initializationRef.current = true;

      const params = parseURLParams();

      // Get URL parameters for pagination
      const searchParams = new URLSearchParams(location.search);
      const urlPage = parseInt(searchParams.get('page')) || 1;
      const urlSize = parseInt(searchParams.get('size')) || 20;

      const initialPage = Math.max(1, urlPage);
      const initialSize = Math.max(1, urlSize);

      // Update all states first
      setCurrentPage(initialPage);
      setItemsPerPage(initialSize);
      setFilterState(params);
      setGlobalSearchTerm(params.globalSearch || '');

      // Check if this is a "Show All Cars" scenario (no filters in URL)
      const hasFiltersInURL = 
        params.selectedCategory || params.selectedBrand || 
        params.selectedModels.length > 0 || params.selectedYears.length > 0 ||
        params.colorSelect || params.carDriveType || params.carEngine ||
        params.carFilterAccessories.length > 0 || params.carFuelType ||
        params.carMileage || params.carPrice || params.carSeat ||
        params.carSteering || params.carTransmission || params.globalSearch;

      const isShowAllCars = !hasFiltersInURL && (urlPage > 1 || urlSize !== 20);

      // Then fetch results - allow fetch even if no filters for initial load
      await fetchResults(params, initialPage - 1, initialSize, true, isShowAllCars);
      setHasInitialized(true);
    };

    initializeData();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // 2. Handle URL changes (back/forward navigation) - SIMPLIFIED
  useEffect(() => {
    if (!hasInitialized || isFetchingRef.current) return;

    const handleURLChange = async () => {
      const newParams = parseURLParams();

      const searchParams = new URLSearchParams(location.search);
      const urlPage = parseInt(searchParams.get('page')) || 1;
      const urlSize = parseInt(searchParams.get('size')) || 20;

      const newPage = Math.max(1, urlPage);
      const newSize = Math.max(1, urlSize);

      // Update state first
      setFilterState(newParams);
      setGlobalSearchTerm(newParams.globalSearch || '');
      setCurrentPage(newPage);
      setItemsPerPage(newSize);

      // Check if this is a "Show All Cars" scenario from URL
      const hasFiltersInURL = 
        newParams.selectedCategory || newParams.selectedBrand || 
        newParams.selectedModels.length > 0 || newParams.selectedYears.length > 0 ||
        newParams.colorSelect || newParams.carDriveType || newParams.carEngine ||
        newParams.carFilterAccessories.length > 0 || newParams.carFuelType ||
        newParams.carMileage || newParams.carPrice || newParams.carSeat ||
        newParams.carSteering || newParams.carTransmission || newParams.globalSearch;

      const isShowAllCars = !hasFiltersInURL;

      // Then fetch - allow fetch for URL changes even with no filters
      await fetchResults(newParams, newPage - 1, newSize, false, isShowAllCars);
    };

    handleURLChange();
  }, [location.search]); // Only depend on location.search

  // 3. Image Fetching
  useEffect(() => {
    if (finalResults && finalResults.length > 0) {
      const newImageCache = { ...imageCache };
      let shouldUpdateCache = false;

      finalResults.forEach((result) => {
        if (!newImageCache[result.carModelId]) {
          newImageCache[result.carModelId] = "/carousel/carslider.png";
          shouldUpdateCache = true;

          fetchCarImage(result.carModelId).then(imageUrl => {
            setImageCache(prev => ({
              ...prev,
              [result.carModelId]: imageUrl
            }));
          });
        }
      });

      if (shouldUpdateCache) {
        setImageCache(newImageCache);
      }
    }
  }, [finalResults, fetchCarImage]);

  // --- Event Handlers ---

  const handleGlobalSearchChange = (event) => {
    const value = event.target.value;
    setGlobalSearchTerm(value);
  };

  const handleGlobalSearchSubmit = () => {
    const updatedState = {
      ...filterState,
      globalSearch: globalSearchTerm
    };

    setFilterState(updatedState);
    fetchResults(updatedState, 0, itemsPerPage, false, false);
  };

  const handleKeyPress = (event) => {
    if (event.key === 'Enter') {
      handleGlobalSearchSubmit();
    }
  };

  // NEW: Handle Show All Cars button click
  const handleShowAllCars = () => {
    const clearedState = {
      selectedCategory: null,
      selectedBrand: null,
      selectedModels: [],
      selectedYears: [],
      colorSelect: '',
      carDriveType: '',
      carEngine: '',
      carFilterAccessories: [],
      carFuelType: '',
      carMileage: '',
      carPrice: '',
      carSeat: '',
      carSteering: '',
      carTransmission: '',
      globalSearch: ''
    };

    setFilterState(clearedState);
    setGlobalSearchTerm('');
    
    // Set the flag to indicate show all cars request
    showAllCarsRequestedRef.current = true;
    
    // Fetch with show all cars flag
    fetchResults(clearedState, 0, itemsPerPage, false, true);
  };

  const handleRemoveFilter = (type, value = null) => {
    const updatedState = { ...filterState };

    switch (type) {
      case 'category':
        updatedState.selectedCategory = null;
        updatedState.selectedBrand = null;
        updatedState.selectedModels = [];
        break;
      case 'brand':
        updatedState.selectedBrand = null;
        updatedState.selectedModels = [];
        break;
      case 'model':
        if (value) {
          updatedState.selectedModels = updatedState.selectedModels.filter(
            m => m.carModelName !== value
          );
        } else {
          updatedState.selectedModels = [];
        }
        break;
      case 'year':
        if (value) {
          updatedState.selectedYears = updatedState.selectedYears.filter(
            y => y.year !== value
          );
        } else {
          updatedState.selectedYears = [];
        }
        break;
      case 'color': updatedState.colorSelect = ''; break;
      case 'driveType': updatedState.carDriveType = ''; break;
      case 'engine': updatedState.carEngine = ''; break;
      case 'accessory':
        updatedState.carFilterAccessories = updatedState.carFilterAccessories.filter(
          a => a !== value
        );
        break;
      case 'fuelType': updatedState.carFuelType = ''; break;
      case 'mileage': updatedState.carMileage = ''; break;
      case 'price': updatedState.carPrice = ''; break;
      case 'seat': updatedState.carSeat = ''; break;
      case 'steering': updatedState.carSteering = ''; break;
      case 'transmission': updatedState.carTransmission = ''; break;
      case 'globalSearch':
        updatedState.globalSearch = '';
        setGlobalSearchTerm('');
        break;
      default: break;
    }

    setFilterState(updatedState);
    fetchResults(updatedState, 0, itemsPerPage, false, false);
  };

  const handleClearAllFilters = () => {
    const clearedState = {
      selectedCategory: null,
      selectedBrand: null,
      selectedModels: [],
      selectedYears: [],
      colorSelect: '',
      carDriveType: '',
      carEngine: '',
      carFilterAccessories: [],
      carFuelType: '',
      carMileage: '',
      carPrice: '',
      carSeat: '',
      carSteering: '',
      carTransmission: '',
      globalSearch: ''
    };

    setFilterState(clearedState);
    setGlobalSearchTerm('');
    
    // When clearing all filters, treat it as show all cars
    showAllCarsRequestedRef.current = true;
    fetchResults(clearedState, 0, itemsPerPage, false, true);
  };

  const handlePageChange = (event, page) => {
    if (event) event.preventDefault();
    if (page === currentPage) return;

    // Check if we're in "show all cars" mode (no active filters)
    const hasActiveFilters = 
      filterState.selectedCategory || filterState.selectedBrand || 
      filterState.selectedModels.length > 0 || filterState.selectedYears.length > 0 ||
      filterState.colorSelect || filterState.carDriveType || filterState.carEngine ||
      filterState.carFilterAccessories.length > 0 || filterState.carFuelType ||
      filterState.carMileage || filterState.carPrice || filterState.carSeat ||
      filterState.carSteering || filterState.carTransmission || filterState.globalSearch;

    const isShowAllCars = !hasActiveFilters;

    fetchResults(filterState, page - 1, itemsPerPage, false, isShowAllCars);
    window.scrollTo(0, 0);
  };

  const handleItemsPerPageChange = (e) => {
    const newItemsPerPage = parseInt(e.target.value);
    setItemsPerPage(newItemsPerPage);
    
    // Check if we're in "show all cars" mode
    const hasActiveFilters = 
      filterState.selectedCategory || filterState.selectedBrand || 
      filterState.selectedModels.length > 0 || filterState.selectedYears.length > 0 ||
      filterState.colorSelect || filterState.carDriveType || filterState.carEngine ||
      filterState.carFilterAccessories.length > 0 || filterState.carFuelType ||
      filterState.carMileage || filterState.carPrice || filterState.carSeat ||
      filterState.carSteering || filterState.carTransmission || filterState.globalSearch;

    const isShowAllCars = !hasActiveFilters;

    fetchResults(filterState, 0, newItemsPerPage, false, isShowAllCars);
  };

  const handleViewMode = (event, newViewMode) => {
    if (newViewMode !== null) {
      setViewMode(newViewMode);
    }
  };

  // Enhanced MoreFilter selection handler
  const handleApplyMoreFilters = (newAdvancedFilters) => {
    const updatedState = {
      ...filterState,
      colorSelect: newAdvancedFilters.colorSelect || '',
      carDriveType: newAdvancedFilters.carDriveType || '',
      carEngine: newAdvancedFilters.carEngine || '',
      carFilterAccessories: newAdvancedFilters.carFilterAccessories || [],
      carFuelType: newAdvancedFilters.carFuelType || '',
      carMileage: newAdvancedFilters.carMileage || '',
      carPrice: newAdvancedFilters.carPrice || '',
      carSeat: newAdvancedFilters.carSeat || '',
      carSteering: newAdvancedFilters.carSteering || '',
      carTransmission: newAdvancedFilters.carTransmission || '',
    };

    setFilterState(updatedState);
    fetchResults(updatedState, 0, itemsPerPage, false, false);
  };

  const handleCarClick = (modelId) => {
    navigate(`/product/${modelId}`);
  };

  const handleAddToWishlist = async (modelId) => {
    try {
      const token = localStorage.getItem("authToken");
      if (!token) { navigate("/login"); return; }
      const cacheBustedUrl = getCacheBustedUrl(
        `${API_BASE_URL}:8084/api/master/car-cart-and-whishlist/add/item`
      );
      const response = await fetch(cacheBustedUrl, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}`, 'Cache-Control': 'no-cache' },
        body: JSON.stringify({ modelId: modelId, type: "ADD_IN_WISHLIST" }),
      });
      const data = await response.json();
      if (data.code === 200) {
        enqueueSnackbar("Added to wishlist successfully", { variant: "success" });
      } else {
        enqueueSnackbar("Failed to add to wishlist", { variant: "error" });
      }
    } catch (error) {
      console.error("Error adding to wishlist:", error);
      enqueueSnackbar("Error adding to wishlist", { variant: "error" });
    }
  };

  const handleAddToCart = async (modelId) => {
    try {
      const token = localStorage.getItem("authToken");
      if (!token) { navigate("/login"); return; }
      const isInCart = cartItems.some((item) => item.modelId === modelId);
      if (isInCart) { enqueueSnackbar("Item is already in your cart", { variant: "warning" }); return; }

      const cacheBustedUrl = getCacheBustedUrl(
        `${API_BASE_URL}:8084/api/master/car-cart-and-whishlist/manage/item?modelId=${modelId}&SelectType=ADD_IN_CART`
      );
      const response = await fetch(cacheBustedUrl, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}`, 'Cache-Control': 'no-cache' },
        body: JSON.stringify({ modelId: modelId, type: "ADD_IN_CART", }),
      });
      const data = await response.json();
      if (data.code === 200) {
        enqueueSnackbar("Added to cart successfully", { variant: "success" });
        dispatch(fetchCart());
      } else {
        enqueueSnackbar(data.message || "Failed to add to cart", { variant: "error", });
      }
    } catch (error) {
      enqueueSnackbar("Error adding to cart", { variant: "error" });
      console.error("Error adding to cart:", error);
    }
  };

  const renderFilterChips = () => {
    if (!filterState) return null;

    const hasAdvancedFilters = filterState.colorSelect || filterState.carDriveType || filterState.carEngine ||
      filterState.carFilterAccessories?.length > 0 || filterState.carFuelType || filterState.carMileage ||
      filterState.carPrice || filterState.carSeat || filterState.carSteering || filterState.carTransmission;

    const hasBasicFilters = filterState.selectedCategory || filterState.selectedBrand ||
      filterState.selectedModels?.length > 0 || filterState.selectedYears?.length > 0;

    const hasGlobalSearch = filterState.globalSearch && filterState.globalSearch.trim() !== '';

    const shouldShowClearAll = hasBasicFilters || hasAdvancedFilters || hasGlobalSearch;

    return (
      <Box sx={{
        display: 'flex',
        flexWrap: 'wrap',
        gap: 1,
        mb: 2,
        alignItems: 'center'
      }}>
        {hasGlobalSearch && (
          <Chip label={`Search: ${filterState.globalSearch}`} onDelete={() => handleRemoveFilter('globalSearch')} sx={{ backgroundColor: '#ffebee', '& .MuiChip-deleteIcon': { color: '#c62828' } }} />
        )}

        {filterState.selectedCategory && (
          <Chip label={`Body: ${filterState.selectedCategory.label}`} onDelete={() => handleRemoveFilter('category')} sx={{ backgroundColor: '#e3f2fd', '& .MuiChip-deleteIcon': { color: '#1976d2' } }} />
        )}
        {filterState.selectedBrand && (
          <Chip label={`Brand: ${filterState.selectedBrand.label}`} onDelete={() => handleRemoveFilter('brand')} sx={{ backgroundColor: '#e8f5e9', '& .MuiChip-deleteIcon': { color: '#2e7d32' } }} />
        )}
        {filterState.selectedModels?.map((model, index) => (
          <Chip key={index} label={`Model: ${model.carModelName}`} onDelete={() => handleRemoveFilter('model', model.carModelName)} sx={{ backgroundColor: '#fff3e0', '& .MuiChip-deleteIcon': { color: '#e65100' } }} />
        ))}
        {filterState.selectedYears?.map((year, index) => (
          <Chip key={index} label={`Year: ${year.year}`} onDelete={() => handleRemoveFilter('year', year.year)} sx={{ backgroundColor: '#f3e5f5', '& .MuiChip-deleteIcon': { color: '#7b1fa2' } }} />
        ))}
        {filterState.colorSelect && (
          <Chip label={`Color: ${filterState.colorSelect}`} onDelete={() => handleRemoveFilter('color')} sx={{ backgroundColor: '#e0f7fa', '& .MuiChip-deleteIcon': { color: '#00838f' } }} />
        )}
        {filterState.carDriveType && (
          <Chip label={`Drive: ${filterState.carDriveType}`} onDelete={() => handleRemoveFilter('driveType')} sx={{ backgroundColor: '#f1f8e9', '& .MuiChip-deleteIcon': { color: '#558b2f' } }} />
        )}
        {filterState.carEngine && (
          <Chip label={`Engine: ${filterState.carEngine}`} onDelete={() => handleRemoveFilter('engine')} sx={{ backgroundColor: '#fffde7', '& .MuiChip-deleteIcon': { color: '#f9a825' } }} />
        )}
        {filterState.carFilterAccessories?.map((accessory, index) => (
          <Chip key={index} label={`${accessory.replace(/_/g, ' ')}`} onDelete={() => handleRemoveFilter('accessory', accessory)} sx={{ backgroundColor: '#fce4ec', '& .MuiChip-deleteIcon': { color: '#ad1457' } }} />
        ))}
        {filterState.carFuelType && (
          <Chip label={`Fuel: ${filterState.carFuelType}`} onDelete={() => handleRemoveFilter('fuelType')} sx={{ backgroundColor: '#e8eaf6', '& .MuiChip-deleteIcon': { color: '#3949ab' } }} />
        )}
        {filterState.carMileage && (
          <Chip label={`Mileage: ${filterState.carMileage}`} onDelete={() => handleRemoveFilter('mileage')} sx={{ backgroundColor: '#e0f2f1', '& .MuiChip-deleteIcon': { color: '#00897b' } }} />
        )}
        {filterState.carPrice && (
          <Chip label={`Price: ${filterState.carPrice}`} onDelete={() => handleRemoveFilter('price')} sx={{ backgroundColor: '#fff8e1', '& .MuiChip-deleteIcon': { color: '#ff8f00' } }} />
        )}
        {filterState.carSeat && (
          <Chip label={`Seats: ${filterState.carSeat}`} onDelete={() => handleRemoveFilter('seat')} sx={{ backgroundColor: '#efebe9', '& .MuiChip-deleteIcon': { color: '#6d4c41' } }} />
        )}
        {filterState.carSteering && (
          <Chip label={`Steering: ${filterState.carSteering}`} onDelete={() => handleRemoveFilter('steering')} sx={{ backgroundColor: '#f5f5f5', '& .MuiChip-deleteIcon': { color: '#616161' } }} />
        )}
        {filterState.carTransmission && (
          <Chip label={`Transmission: ${filterState.carTransmission}`} onDelete={() => handleRemoveFilter('transmission')} sx={{ backgroundColor: '#ede7f6', '& .MuiChip-deleteIcon': { color: '#5e35b1' } }} />
        )}

        {shouldShowClearAll && (
          <Button
            variant="outlined" size="small" onClick={handleClearAllFilters}
            sx={{ ml: 1, textTransform: 'none', color: 'error.main', borderColor: 'error.main', '&:hover': { backgroundColor: 'error.light', borderColor: 'error.main' } }}
            startIcon={<ClearIcon fontSize="small" />}
          >
            Clear All
          </Button>
        )}
      </Box>
    );
  };

  // Add Show All Cars button when no results are shown
  const renderShowAllButton = () => {
    const hasAnyFilters = 
      filterState.selectedCategory || filterState.selectedBrand || 
      filterState.selectedModels.length > 0 || filterState.selectedYears.length > 0 ||
      filterState.colorSelect || filterState.carDriveType || filterState.carEngine ||
      filterState.carFilterAccessories.length > 0 || filterState.carFuelType ||
      filterState.carMileage || filterState.carPrice || filterState.carSeat ||
      filterState.carSteering || filterState.carTransmission || filterState.globalSearch;

    if (!hasAnyFilters && finalResults.length === 0 && !isLoading) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
          <Button 
            variant="contained" 
            color="primary" 
            onClick={handleShowAllCars}
            sx={{ px: 4, py: 1.5, fontSize: '1rem' }}
          >
            Show All Cars
          </Button>
        </Box>
      );
    }
    return null;
  };

  return (
    <Box className="container-main-support" sx={{ width: "100%" }}>
      <Box sx={{ marginBottom: "0%" }}><Notes /></Box>
      <Container maxWidth={false} sx={{ padding: 0, margin: 0 }}>
        <Container maxWidth="xl" sx={{ mt: 4, mb: 8 }}>
          {renderFilterChips()}

          <Paper elevation={0} sx={{ p: 3, mb: 4, borderRadius: '10px', backgroundColor: '#f9f9f9' }}>
            <Box sx={{
              display: "flex", flexDirection: isMobile ? "column" : "row",
              justifyContent: "space-between", alignItems: isMobile ? "flex-start" : "center",
              mb: isMobile ? 2 : 0, gap: isMobile ? 2 : 0,
            }}>
              <Box>
                <Typography variant="h5" sx={{ mb: 1, fontWeight: 'bold' }}>
                  Search Results
                </Typography>
                <Typography variant="body1" color="text.secondary">
                  {totalItems} {totalItems === 1 ? 'result' : 'results'} found
                </Typography>
              </Box>

              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Typography variant="body2" sx={{ mr: 1, display: { xs: 'none', sm: 'block' } }}>
                  View:
                </Typography>
                <ToggleButtonGroup
                  value={viewMode} exclusive onChange={handleViewMode} size="small"
                  sx={{
                    '& .MuiToggleButton-root': {
                      border: '1px solid rgba(0, 0, 0, 0.12)',
                      '&.Mui-selected': { backgroundColor: '#ff3d00', color: 'white', '&:hover': { backgroundColor: '#dd3700', }, },
                    },
                  }}
                >
                  <ToggleButton value="list"><ListIcon /></ToggleButton>
                  <ToggleButton value="grid"><GridView /></ToggleButton>
                </ToggleButtonGroup>
              </Box>
            </Box>
          </Paper>

          {isLoading ? (
            <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "50vh" }}>
              <SpinningLogo src="/image/footer-logo-small.svg" alt="Loading" />
            </Box>
          ) : finalResults.length > 0 ? (
            viewMode === "list" ? (
              <CarListingTable
                cars={finalResults} onCarClick={handleCarClick} onAddToWishlist={handleAddToWishlist}
                onAddToCart={handleAddToCart} imageUrl={imageCache}
              />
            ) : (
              <Grid container spacing={3}>
                {finalResults.map((result) => (
                  <Grid item xs={12} sm={6} md={4} lg={3} key={result.carModelId}>
                    <CarListingCardGrid
                      car={result} onCarClick={handleCarClick} onAddToWishlist={handleAddToWishlist}
                      onAddToCart={handleAddToCart} imageUrl={imageCache[result.carModelId] || "/carousel/carslider.png"}
                    />
                  </Grid>
                ))}
              </Grid>
            )
          ) : (
            <Box sx={{
              display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
              minHeight: '50vh', backgroundColor: '#f9f9f9', borderRadius: 2, p: 4
            }}>
              <Typography variant="h6" sx={{ textAlign: "center", color: "#666", mb: 2 }}>
                No search results found
              </Typography>
              <Typography variant="body1" sx={{ textAlign: "center", color: "#888", mb: 3 }}>
                Try different search criteria or show all cars.
              </Typography>
              {renderShowAllButton()}
            </Box>
          )}

          {finalResults.length > 0 && (
            <Box
              sx={{
                display: "flex", justifyContent: "space-between", alignItems: "center", mt: 3, mb: 4,
                flexDirection: isMobile ? "column" : "row", gap: 2,
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mt: 1 }}>
                <Typography variant="body2" sx={{ color: "black", fontWeight: 500, letterSpacing: "0.5px" }}>
                  Results per page:
                </Typography>
              </Box>

              <Pagination
                count={totalPages}
                page={currentPage}
                onChange={handlePageChange}
                variant="outlined" shape="rounded"
                sx={{
                  display: "flex", justifyContent: "center", mt: 3,
                  "& .MuiPaginationItem-root": { fontWeight: 500, borderColor: "black", color: "black", "&:hover": { backgroundColor: "rgba(255, 0, 0, 0.1)", borderColor: "red", }, },
                  "& .Mui-selected": { backgroundColor: "red !important", color: "white", borderColor: "red", "&:hover": { backgroundColor: "#b30000 !important", borderColor: "#800000", }, },
                }}
              />

              <Select
                value={itemsPerPage}
                onChange={handleItemsPerPageChange}
                variant="outlined" displayEmpty size="small"
                sx={{
                  width: 80, color: "black", fontWeight: 500, backgroundColor: "#fff", borderColor: "black",
                  "& .MuiOutlinedInput-notchedOutline": { borderColor: "black", },
                  "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "red", },
                  "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: "red", },
                  "& .MuiSvgIcon-root": { color: "red", },
                }}
              >
                <MenuItem value={20}>20</MenuItem>
                <MenuItem value={50}>50</MenuItem>
                <MenuItem value={100}>100</MenuItem>
              </Select>
            </Box>
          )}
          
        </Container>

        <FooterManual />
      </Container>
    </Box>
  );
};

export default MoreFilterByGlobalSearch;